import React, { Fragment, useEffect, useMemo, useState, useRef } from "react";
import {
  FaPlus,
  FaMinus,
  FaExclamationTriangle,
  FaInfoCircle,
} from "react-icons/fa";
import { GoGraph } from "react-icons/go";
import { IoChevronForwardSharp } from "react-icons/io5";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion";
import { Prompt } from "react-router-dom";
import Switch from "react-switch";
import PickDate from "../../components/common/datePicker";
import CustomFooter from "../../components/customFooter";
import GetFunctionalSummary from "../../hooks/getFunctionalSummary";
import GetFunctionalChecklist from "../../hooks/getFunctionalChecklist";
import useSubmitTeamScores from "../../hooks/useSubmitTeamScores";
import moment from "moment";
import { formatInEt } from "../../utils/helper";
import { Link } from "react-router-dom";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import jsPDF from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import { getScore } from "../../services/plan_service";
import { BsChevronDown, BsChevronUp } from "react-icons/bs";
import { toast, Bounce } from "react-toastify";
import "react-toastify/ReactToastify.css";
import usePreventBrowserBack from "../../hooks/usePreventBrowserBack";
import {
  NOT_APPLICABLE_LABEL,
  formatScoreValue,
  hasScore,
  toScoreValue,
} from "../../utils/scoreState";
import {
  getScaleValues,
  getScorePillProps,
  getScoreRgb,
} from "../../utils/scoringScale";

// Apply the plugin to jsPDF
applyPlugin(jsPDF);

// The colour of a score comes from the scale its heading is rated on, so a
// value only ever gets a swatch the scale actually defines for it.
const drawPdfScoreCircle = (doc, cell, rawScore, scale, scoringScale) => {
  const score = getNumericScore(rawScore, scoringScale);
  if (score === null) return;

  const [r, g, b] = getScoreRgb(scoringScale, score) || [175, 177, 184];
  const radius = 3.4 * scale;
  const centerX = cell.x + cell.width / 2;
  const centerY = cell.y + cell.height / 2;

  doc.setFillColor(r, g, b);
  doc.circle(centerX, centerY, radius, "F");
  doc.setTextColor(0, 0, 0);
  doc.setFont("Quicksand", "normal");
  doc.setFontSize(9 * scale);
  doc.text(String(score), centerX, centerY + 1.1 * scale, {
    align: "center",
  });
  doc.setFont("Quicksand", "normal");
};

const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunkSize = 0x8000;

  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(
      ...bytes.subarray(index, index + chunkSize),
    );
  }

  return btoa(binary);
};

let pdfAssetsPromise = null;

const loadPdfAssets = () => {
  if (!pdfAssetsPromise) {
    pdfAssetsPromise = Promise.all([
      fetch("/normal_logo.png").then((response) => {
        if (!response.ok) throw new Error("Unable to load the POMMe logo");
        return response.arrayBuffer();
      }),
      fetch("/fonts/Quicksand.ttf").then((response) => {
        if (!response.ok) throw new Error("Unable to load the Quicksand font");
        return response.arrayBuffer();
      }),
    ]).then(([logoBuffer, fontBuffer]) => ({
      logoDataUrl: `data:image/png;base64,${arrayBufferToBase64(logoBuffer)}`,
      quicksandFontBase64: arrayBufferToBase64(fontBuffer),
    }));
  }

  return pdfAssetsPromise;
};

const resetPdfPage = (doc) => {
  doc.setFillColor(255, 255, 255);
  doc.rect(
    0,
    0,
    doc.internal.pageSize.width,
    doc.internal.pageSize.height,
    "F",
  );
};

const addPdfHeader = (
  doc,
  logoDataUrl,
  patientData,
  reportStartDate,
  reportEndDate,
) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const scale = 0.75;
  const headerMarginX = 7;
  const patientRightX = pageWidth - headerMarginX;

  resetPdfPage(doc);
  doc.setTextColor(0, 0, 0);
  doc.addImage(logoDataUrl, "PNG", headerMarginX, 7, 30, 13);

  doc.setFont("Quicksand", "normal");
  doc.setFontSize(16 * scale);
  doc.text("Functional Summaries Report", pageWidth / 2, 13, {
    align: "center",
  });

  doc.setFontSize(11 * scale);
  if (reportStartDate && reportEndDate) {
    const dateRange = `${moment(reportStartDate).format(
      "DD MMM YYYY",
    )} to ${moment(reportEndDate).format("DD MMM YYYY")}`;
    doc.text(`Time period: ${dateRange}.`, pageWidth / 2, 18, {
      align: "center",
    });
  }

  const patientSummary = [
    patientData?.lastName && patientData?.firstName
      ? `${patientData.lastName}, ${patientData.firstName}`
      : patientData?.lastName || patientData?.firstName || "",
    patientData?.age == null ? "" : String(patientData.age),
    patientData?.gender || "",
  ]
    .filter(Boolean)
    .join(" ");

  doc.text(patientSummary, patientRightX, 12, { align: "right" });
  doc.text(
    `DOB: ${
      patientData?.dob ? moment(patientData.dob).format("DD MMM YYYY") : ""
    }`,
    patientRightX,
    16,
    { align: "right" },
  );
  doc.text(`OHIP #: ${patientData?.ohip || ""}`, patientRightX, 20, {
    align: "right",
  });
};

// Styling for the pill that shows the team score the user just picked. An N/A
// pick reads as "no score" here, so it gets the neutral pill rather than a
// colour from the scale.
const teamScorePillProps = (value, scoringScale) =>
  value === NOT_APPLICABLE_LABEL
    ? { className: "head_light f-10", style: undefined }
    : getScorePillProps(scoringScale, value);

// N/A is displayed exactly like an unanswered item on this page: blank. So the
// only thing ever rendered for a score is a value the heading's scale defines.
const normalizeScore = (score, scoringScale) =>
  formatScoreValue(score, "", scoringScale);

// The score of one contributor entry: the number when answered, blank for both
// N/A and unanswered.
const contributorScore = (item, scoringScale) =>
  formatScoreValue(item?.score, "", scoringScale);

// Label inside the team-score pill — blank for an N/A pick, matching the rest
// of the page.
const teamScorePillLabel = (value) =>
  value === NOT_APPLICABLE_LABEL ? "" : value;

// The median pill for one subheading. Medians come back as decimals, so they
// are floored onto the heading's scale before a colour is picked — the same
// rounding the API applies to its own median.
const medianPill = (subheading, scoringScale) => {
  const median = getNumericScore(
    subheading?.median == null ? null : Math.floor(subheading.median),
    scoringScale,
  );
  if (median === null) return { className: "bg_light", style: undefined };
  return getScorePillProps(scoringScale, median, "bg_light");
};

// Helper function to get numeric score for comparisons
const getNumericScore = (score, scoringScale) =>
  toScoreValue(score, scoringScale);

// Who entered one assessment, keyed by entryId. Answers name only the entry
// they were given with, so the provider, their position and the assessment date
// are read back off any score that entry recorded — every score under one entry
// carries the same three.
const buildEntryMap = (headings = []) => {
  const entries = new Map();
  for (const heading of headings || []) {
    for (const subheading of heading?.subheadings || []) {
      for (const score of subheading?.collaboratorScores || []) {
        if (!score?.entryId || entries.has(score.entryId)) continue;
        entries.set(score.entryId, {
          providerId: score.providerId,
          firstname: score.firstname,
          lastname: score.lastname,
          position: score.position,
          assessmentDate: score.assessmentDate,
        });
      }
    }
  }
  return entries;
};

// The Questions section: the flat answers the API returns, grouped by question
// and joined back to whoever gave them.
//
// An answer whose entry is not in the result still shows — its scores may have
// been filtered out, or it may have been an entry with no score at all — it
// just has nobody's name against it, which is the honest reading rather than
// dropping the answer.
const buildQuestionsData = (questionAnswers = [], headings = []) => {
  const entries = buildEntryMap(headings);
  const byQuestion = new Map();

  for (const answer of questionAnswers || []) {
    if (!answer?.questionId || !answer?.answer) continue;
    if (!byQuestion.has(answer.questionId)) {
      byQuestion.set(answer.questionId, {
        id: answer.questionId,
        questionText: answer.questionText || "",
        responses: [],
      });
    }
    const entry = entries.get(answer.entryId) || {};
    byQuestion.get(answer.questionId).responses.push({
      id: `${answer.questionId}-${answer.entryId}`,
      entryId: answer.entryId,
      providerId: entry.providerId,
      firstname: entry.firstname || "",
      lastname: entry.lastname || "",
      position: entry.position || null,
      // The assessment the answer was given with; the entry is what dates it,
      // and dateCreated is only when the row was written.
      assessmentDate: entry.assessmentDate || answer.dateCreated,
      answer: answer.answer,
    });
  }

  // Questions with no answer in this result are left out entirely — the
  // section only ever lists what somebody actually answered.
  return Array.from(byQuestion.values()).filter(
    (question) => question.responses.length > 0,
  );
};

// Every distinct contributor that appears in a summaries payload, deduped by
// providerId. Driven off a payload that is NOT narrowed by the provider/score/
// issue filters, so the filter list keeps offering everyone in the date range
// instead of collapsing to whoever is currently selected.
const extractProviders = (summaryData) => {
  const providersMap = new Map();
  (summaryData || []).forEach((heading) => {
    (heading.subheadings || []).forEach((subheading) => {
      (subheading.collaboratorScores || [])
        .filter((score) => hasScore(score, heading.scoringScale))
        .forEach((score) => {
          if (!score.email || !score.providerId) return;
          if (providersMap.has(score.providerId)) return;
          const providerKey = `${score.lastname}, ${score.firstname}`;
          const positionAbbr = score.position?.abbreviation || "N/A";
          providersMap.set(score.providerId, {
            id: score.providerId,
            name: providerKey,
            position: positionAbbr,
            fullName: `${providerKey}: ${positionAbbr}`,
          });
        });
    });
  });
  return Array.from(providersMap.values());
};

const FilterLozenge = ({ label, count, className = "", isOpen, onClick }) => (
  <button
    type="button"
    className={`functional-filter-lozenge ${
      count !== undefined
        ? "functional-filter-lozenge-has-count"
        : "functional-filter-lozenge-no-count"
    } ${className}`}
    aria-expanded={isOpen}
    onClick={onClick}
  >
    <span className="functional-filter-lozenge-label">{label}</span>
    {count !== undefined && (
      <span className="functional-filter-lozenge-count">{count}</span>
    )}
    <BsChevronDown className="functional-filter-lozenge-caret" size={14} />
  </button>
);

const FilterDropdown = ({
  id,
  label,
  count,
  className,
  panelClassName,
  openDropdown,
  onToggle,
  children,
}) => {
  const isOpen = openDropdown === id;

  return (
    <div className="functional-filter-dropdown">
      <FilterLozenge
        label={label}
        count={count}
        className={className}
        isOpen={isOpen}
        onClick={() => onToggle(id)}
      />
      {isOpen && (
        <div className={`functional-filter-menu ${panelClassName || ""}`}>
          {children}
        </div>
      )}
    </div>
  );
};

const FunctionalSummaries = (props) => {
  usePreventBrowserBack(Boolean(props.location?.state?.preventBrowserBack));
  const graphFilters = props.location?.state?.graphFilters;
  const parseStateDate = (value) =>
    value && moment(value).isValid() ? moment(value).toDate() : null;
  const [active, setActive] = useState([]);
  const [startDate, setStartDate] = useState(
    graphFilters?.startDate
      ? parseStateDate(graphFilters.startDate)
      : props.location?.state?.fromDate
        ? moment(props.location?.state?.fromDate).toDate()
        : null,
  );
  const [endDate, setEndDate] = useState(
    graphFilters?.endDate
      ? parseStateDate(graphFilters.endDate)
      : props.location?.state?.toDate
        ? moment(props.location?.state?.toDate).toDate()
        : null,
  );
  const [reportStartDate, setReportStartDate] = useState(null);
  const [reportEndDate, setReportEndDate] = useState(null);
  const [filterStartDate, setFilterStartDate] = useState(
    graphFilters?.filterStartDate || null,
  );
  const [filterEndDate, setFilterEndDate] = useState(
    graphFilters?.filterEndDate || null,
  );
  const [selectedScore, setSelectedScore] = useState(
    graphFilters?.selectedScore ?? null,
  );
  const [filterScores, setFilterScores] = useState(
    graphFilters?.filterScores || null,
  );
  const [selectedProviders, setSelectedProviders] = useState(
    graphFilters?.selectedProviders || [],
  );
  const [filterProviders, setFilterProviders] = useState(
    graphFilters?.filterProviders || null,
  );
  const [selectedFunctionalIssues, setSelectedFunctionalIssues] = useState(
    graphFilters?.selectedFunctionalIssues || [],
  );
  const [filterFunctionalIssues, setFilterFunctionalIssues] = useState(
    graphFilters?.filterFunctionalIssues || null,
  );
  const [showSignModal, setShowSignModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [questionsData, setQuestionsData] = useState([]);
  const [questionsActive, setQuestionsActive] = useState([]);
  const patient = localStorage.getItem("patient");

  // Navigation confirmation states
  const [showNavigationModal, setShowNavigationModal] = useState(false);
  const [pendingLocation, setPendingLocation] = useState(null);
  const [allowNavigation, setAllowNavigation] = useState(false);
  const [hasTeamScoresBeenModified, setHasTeamScoresBeenModified] =
    useState(false);
  // Offer to carry the medians into the care plan as the team scores, asked on
  // the way out when nothing was scored here.
  const [showUseMediansModal, setShowUseMediansModal] = useState(false);
  const [checked, setChecked] = useState(
    graphFilters?.checked || { check: false, attr: "" },
  );
  const [checkedScores, setCheckedScores] = useState(
    graphFilters?.checkedScores || {
      check: false,
      attr: "",
    },
  );
  const [checkedNames, setCheckedNames] = useState(
    graphFilters?.checkedNames || { check: false, attr: "" },
  );
  const [openFilterDropdown, setOpenFilterDropdown] = useState(null);
  const [draftStartDate, setDraftStartDate] = useState(startDate);
  const [draftEndDate, setDraftEndDate] = useState(endDate);
  const [draftSingleDate, setDraftSingleDate] = useState(null);
  const [draftFunctionalIssues, setDraftFunctionalIssues] = useState(
    selectedFunctionalIssues,
  );
  const [draftScore, setDraftScore] = useState(selectedScore);
  const [draftProviders, setDraftProviders] = useState(selectedProviders);
  const [expandedFilterHeadingIds, setExpandedFilterHeadingIds] = useState([]);
  // entryId of the batch the user checked to amend (null = nothing selected)
  const [selectedBatchEntryId, setSelectedBatchEntryId] = useState(null);
  const filterBarRef = useRef(null);
  const fromDate = props.location?.state?.fromDate;
  const toDate = props.location?.state?.toDate;
  const formattedFromDate =
    !graphFilters && fromDate && moment(fromDate).isValid()
      ? moment(fromDate).startOf("day").format("YYYY-MM-DDTHH:mm:ss.SSS")
      : null;
  const formattedToDate =
    !graphFilters && toDate && moment(toDate).isValid()
      ? moment(toDate).startOf("day").format("YYYY-MM-DDTHH:mm:ss.SSS")
      : null;
  const getDefaultTimePeriod = () => {
    const end = moment().startOf("day");
    const start = moment(end).subtract(6, "days");
    return {
      startDate: start.toDate(),
      endDate: end.toDate(),
      formattedStartDate: start.format("YYYY-MM-DDTHH:mm:ss.SSS"),
      formattedEndDate: end.format("YYYY-MM-DDTHH:mm:ss.SSS"),
    };
  };
  const { data, isLoading } = GetFunctionalSummary(
    patient,
    filterStartDate || formattedFromDate || null,
    filterEndDate || formattedToDate || null,
    filterScores,
    filterProviders,
    filterFunctionalIssues,
  );
  // Same date range as the main query but with none of the narrowing filters,
  // so it always answers "who has records in this window?". React Query keys on
  // the arguments, so while no narrowing filter is active this resolves to the
  // very same cache entry as `data` and costs no extra request.
  const { data: providerRosterData } = GetFunctionalSummary(
    patient,
    filterStartDate || formattedFromDate || null,
    filterEndDate || formattedToDate || null,
    null,
    null,
    null,
  );
  const availableProviders = useMemo(
    () => extractProviders(providerRosterData?.data),
    [providerRosterData],
  );
  const { data: functionalIssues, isLoading: checklistLoading } =
    GetFunctionalChecklist();
  useEffect(() => {
    if (graphFilters) {
      setStartDate(parseStateDate(graphFilters.startDate));
      setEndDate(parseStateDate(graphFilters.endDate));
      setReportStartDate(parseStateDate(graphFilters.startDate));
      setReportEndDate(parseStateDate(graphFilters.endDate));
      setFilterStartDate(graphFilters.filterStartDate || null);
      setFilterEndDate(graphFilters.filterEndDate || null);
      return;
    }

    if (
      fromDate &&
      toDate &&
      moment(fromDate).isValid() &&
      moment(toDate).isValid()
    ) {
      setStartDate(moment(fromDate).toDate());
      setEndDate(moment(toDate).toDate());
      setReportStartDate(moment(fromDate));
      setReportEndDate(moment(toDate));
      setFilterStartDate(formattedFromDate);
      setFilterEndDate(formattedToDate);
    } else {
      // Default to last 7 days
      const defaultTimePeriod = getDefaultTimePeriod();
      setStartDate(defaultTimePeriod.startDate);
      setEndDate(defaultTimePeriod.endDate);
      setReportStartDate(defaultTimePeriod.startDate);
      setReportEndDate(defaultTimePeriod.endDate);
      setFilterStartDate(defaultTimePeriod.formattedStartDate);
      setFilterEndDate(defaultTimePeriod.formattedEndDate);
    }
  }, [graphFilters, fromDate, toDate, formattedFromDate, formattedToDate]);

  useEffect(() => {
    if (
      startDate &&
      endDate &&
      moment(startDate).isValid() &&
      moment(endDate).isValid()
    ) {
      setReportStartDate(startDate);
      setReportEndDate(endDate);
    }
  }, [startDate, endDate]);
  // Initialize team scores hook
  const {
    isLoading: isSubmittingScores,
    updateScore: updateTeamScore,
    clearScore: clearTeamScore,
    submitScores: submitTeamScores,
    getSelectedScore: getTeamSelectedScore,
    shouldShowDropdown,
  } = useSubmitTeamScores();

  // Ref for print functionality
  const printRef = useRef();
  useEffect(() => {
    if (data?.data && data?.data?.length > 0) {
      const momentDates = [];

      data.data.forEach((item) => {
        // Collect dates from various sources in the data
        if (item.dateCreated && moment(item.dateCreated).isValid()) {
          momentDates.push(moment(item.dateCreated));
        }
        if (item.dateUpdated && moment(item.dateUpdated).isValid()) {
          momentDates.push(moment(item.dateUpdated));
        }
        if (item.fromDate && moment(item.fromDate).isValid()) {
          momentDates.push(moment(item.fromDate));
        }
        if (item.toDate && moment(item.toDate).isValid()) {
          momentDates.push(moment(item.toDate));
        }

        // Also collect dates from subheadings if they exist (for functional summaries)
        if (item.subheadings && Array.isArray(item.subheadings)) {
          item.subheadings.forEach((sub) => {
            if (
              sub.collaboratorScores &&
              Array.isArray(sub.collaboratorScores)
            ) {
              sub.collaboratorScores
                .filter((score) => hasScore(score, item.scoringScale))
                .forEach((score) => {
                  if (
                    score.assessmentDate &&
                    moment(score.assessmentDate).isValid()
                  ) {
                    momentDates.push(moment(score.assessmentDate));
                  }
                });
            }
          });
        }
      });

      // The filter dates always describe the range the displayed data was
      // fetched for, so they win. This used to be skipped whenever the page was
      // opened with fromDate/toDate in navigation state, which pinned the report
      // range to that original window for the life of the page — team scores
      // were then saved against a range the user was no longer looking at.
      if (filterStartDate && filterEndDate) {
        setReportStartDate(moment(filterStartDate));
        setReportEndDate(moment(filterEndDate));
      } else if (momentDates?.length > 0) {
        const minDate = moment.min(momentDates);
        const maxDate = moment.max(momentDates);
        setReportStartDate(minDate);
        setReportEndDate(maxDate);
      } else if (reportStartDate == null || startDate == null) {
        const minDate = moment.min(momentDates);
        setReportStartDate(minDate);
      } else if (reportEndDate == null || endDate == null) {
        const maxDate = moment.max(momentDates);
        setReportEndDate(maxDate);
      }
      if (data.data[0]?.subheadings?.[0]?.subheadingName) {
        setActive([data.data[0].subheadings[0].subheadingName]);
      }
    }

    // The answers given alongside these scores. They come back at the root of
    // the response rather than under a subheading, because an answer belongs to
    // the assessment as a whole — and they are built whether or not any score
    // survived the filters, so an assessment that only answered questions still
    // shows up.
    const questions = buildQuestionsData(data?.questionAnswers, data?.data);
    setQuestionsData(questions);
    setQuestionsActive(questions.length ? [questions[0].id] : []);
  }, [data]);
  useEffect(() => {
    moment.updateLocale("en", {
      monthsShort: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sept",
        "Oct",
        "Nov",
        "Dec",
      ],
    });
  }, []);

  // Handle browser close/refresh warning
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasTeamScoresBeenModified && !allowNavigation) {
        e.preventDefault();
        e.returnValue = ""; // Chrome requires returnValue to be set
        return ""; // Some browsers show this message
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasTeamScoresBeenModified, allowNavigation]);

  // A dropdown panel is absolutely positioned under its lozenge, so the room it
  // has is the distance from the filter bar to the bottom of the window — which
  // depends on how far down the page the bar sits and is therefore not knowable
  // in CSS. Measure it and hand it to the stylesheet as a custom property; the
  // panels inherit it because they render inside the bar.
  useEffect(() => {
    const updateMenuMaxHeight = () => {
      const bar = filterBarRef.current;
      if (!bar) return;
      // 11px clears the gap between the lozenge and the panel top; 24px keeps
      // the panel off the very bottom edge of the window.
      const available =
        window.innerHeight - bar.getBoundingClientRect().bottom - 11 - 24;
      bar.style.setProperty(
        "--functional-filter-menu-max-height",
        `${Math.max(available, 200)}px`,
      );
    };

    updateMenuMaxHeight();
    window.addEventListener("resize", updateMenuMaxHeight);
    return () => window.removeEventListener("resize", updateMenuMaxHeight);
  }, [openFilterDropdown]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        openFilterDropdown &&
        filterBarRef.current &&
        !filterBarRef.current.contains(event.target)
      ) {
        setOpenFilterDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [openFilterDropdown]);

  useEffect(() => {
    if (openFilterDropdown === "time") {
      const nextStartDate =
        filterStartDate && moment(filterStartDate).isValid()
          ? moment(filterStartDate).toDate()
          : startDate;
      const nextEndDate =
        filterEndDate && moment(filterEndDate).isValid()
          ? moment(filterEndDate).toDate()
          : endDate;
      setDraftStartDate(nextStartDate);
      setDraftEndDate(nextEndDate);
      // Reflect a single-day selection only when start and end are the same day
      setDraftSingleDate(
        nextStartDate &&
          nextEndDate &&
          moment(nextStartDate).isSame(nextEndDate, "day")
          ? nextStartDate
          : null,
      );
    }

    if (openFilterDropdown === "issues") {
      setDraftFunctionalIssues(filterFunctionalIssues || []);
      const firstHeadingId = functionalIssues?.data?.[0]?.headings?.[0]?.id;
      setExpandedFilterHeadingIds(firstHeadingId ? [firstHeadingId] : []);
    }

    if (openFilterDropdown === "score") {
      setDraftScore(
        selectedScore ||
          (filterScores?.length > 0 ? Math.max(...filterScores) : null),
      );
    }

    if (openFilterDropdown === "providers") {
      setDraftProviders(filterProviders || []);
    }
  }, [
    openFilterDropdown,
    filterStartDate,
    filterEndDate,
    startDate,
    endDate,
    filterFunctionalIssues,
    functionalIssues,
    selectedScore,
    filterScores,
    filterProviders,
  ]);

  const handleChecked = (status, e, id) => {
    switch (id) {
      case "function":
        setChecked({ check: status, attr: id });
        // If functional issues switch is turned off, clear selected functional issues
        if (!status) {
          setSelectedFunctionalIssues([]);
        }
        break;
      case "score":
        setCheckedScores({ check: status, attr: id });
        // If score switch is turned off, clear selected score
        if (!status) {
          setSelectedScore(null);
        }
        break;
      case "names":
        setCheckedNames({ check: status, attr: id });
        // If names switch is turned off, clear selected providers
        if (!status) {
          setSelectedProviders([]);
        }
        break;
      default:
        break;
    }
  };

  const addActiveSubheading = (subheading) => {
    setActive([...active, subheading]);
  };
  const removeActiveSubheading = (index) => {
    const updatedArray = [...active];
    updatedArray.splice(index, 1);
    setActive(updatedArray);
  };

  // Questions functions
  const addActiveQuestion = (question) => {
    setQuestionsActive([...questionsActive, question]);
  };
  const removeActiveQuestion = (index) => {
    const updatedArray = [...questionsActive];
    updatedArray.splice(index, 1);
    setQuestionsActive(updatedArray);
  };

  // Date filter functions
  const handleTodayFilter = () => {
    const today = moment().startOf("day").toDate();

    setStartDate(today);
    setEndDate(today);
  };

  const handleLast7DaysFilter = () => {
    const sevenDaysAgo = moment().subtract(7, "days").startOf("day").toDate();
    const today = moment().startOf("day").toDate();

    setStartDate(sevenDaysAgo);
    setEndDate(today);
  };

  const handleLast30DaysFilter = () => {
    const thirtyDaysAgo = moment().subtract(30, "days").startOf("day").toDate();
    const today = moment().startOf("day").toDate();

    setStartDate(thirtyDaysAgo);
    setEndDate(today);
  };

  // Format date for API (YYYY-MM-DDTHH:mm:ss.SSS)
  const formatDateForAPI = (date) => {
    if (!date || !moment(date).isValid()) return null;
    // Use moment to preserve the exact date without timezone offset
    return moment(date).startOf("day").format("YYYY-MM-DDTHH:mm:ss.SSS");
  };

  // Handle score selection (radio button logic)
  const handleScoreSelection = (score) => {
    // If the same score is clicked again, deselect it
    if (selectedScore === score) {
      setSelectedScore(null);
    } else {
      setSelectedScore(score);
    }
  };

  // Handle team score selection for subheadings
  const handleTeamScoreSelection = (subheadingId, scoreValue) => {
    if (getTeamSelectedScore(subheadingId) === scoreValue) {
      clearTeamScore(subheadingId);
      setHasTeamScoresBeenModified(true);
      sessionStorage.setItem("hasUnsavedTeamScores", "true");
      return;
    }

    updateTeamScore(subheadingId, scoreValue);
    setHasTeamScoresBeenModified(true); // Mark that team scores have been modified
    sessionStorage.setItem("hasUnsavedTeamScores", "true"); // Store in sessionStorage for logout detection
  };

  // Navigation blocking function
  const handleNavigationAttempt = (location) => {
    if (allowNavigation || !hasTeamScoresBeenModified) {
      return true; // Allow navigation
    }

    // Don't show warning for dropdown anchor links (they just add # to URL)
    if (
      location.pathname === window.location.pathname &&
      location.hash === "#"
    ) {
      return true;
    }

    setPendingLocation(location);
    setShowNavigationModal(true);
    return false; // Block navigation
  };

  // Confirm navigation and lose data
  const handleConfirmNavigation = () => {
    setAllowNavigation(true);
    setShowNavigationModal(false);
    sessionStorage.removeItem("hasUnsavedTeamScores"); // Clear sessionStorage flag

    // Navigate to the intended location
    if (pendingLocation) {
      props.history.push(pendingLocation.pathname);
    }
  };

  // Cancel navigation
  const handleCancelNavigation = () => {
    setShowNavigationModal(false);
    setPendingLocation(null);
  };

  // Functional issues left without a team score that the median could stand in
  // for. Per the API an explicit N/A is an answer, not a gap, so it doesn't
  // count as missing — and a gap with no median behind it has nothing to offer.
  const hasMediansForMissingTeamScores = useMemo(
    () =>
      (data?.data || []).some((heading) =>
        (heading.subheadings || []).some(
          (sub) =>
            sub.teamScore == null &&
            !sub.teamScoreNotApplicable &&
            sub.median != null,
        ),
      ),
    [data],
  );

  // Leave for the care plan without saving anything. Matches the sidebar's
  // Create Care Plan navigation: no team-score submission, no preselected type.
  const goToCarePlanWithoutTeamScores = () => {
    setShowUseMediansModal(false);
    setAllowNavigation(true);
    localStorage.removeItem("type");
    props.history.push("/hcp/patient/care_management");
  };

  // Save the medians as the team's scores for this period, then take the user
  // to the collaborative care plan. The batch is written here — the care plan
  // then reads real team scores like any other plan and needs no median logic
  // of its own.
  const goToCarePlanWithMedians = async () => {
    // The scale travels with each row: the batch is flat, but the headings it
    // spans may be scored on different ranges.
    const allSubheadings = (data?.data || []).reduce(
      (acc, heading) =>
        acc.concat(
          (heading.subheadings || []).map((subheading) => ({
            ...subheading,
            scoringScale: heading.scoringScale,
          })),
        ),
      [],
    );

    const batchId = await submitTeamScores(
      localStorage.getItem("patient"),
      allSubheadings,
      reportStartDate,
      reportEndDate,
      // Subheadings the team already scored — and explicit N/As — are left
      // exactly as they are; only the gaps take a median.
      { useMedianForUnscored: true },
    );

    if (!batchId) {
      toast.error(
        "Could not save the medians as team scores. Please try again.",
        {
          position: "top-right",
          autoClose: 4000,
          theme: "light",
          transition: Bounce,
        },
      );
      return;
    }

    setShowUseMediansModal(false);
    setHasTeamScoresBeenModified(false);
    sessionStorage.removeItem("hasUnsavedTeamScores");
    setAllowNavigation(true);
    props.history.push({
      pathname: "/hcp/patient/care_management",
      state: {
        fromTeamScores: true,
        date: new Date(),
        fromDate: formatDateForAPI(reportStartDate),
        toDate: formatDateForAPI(reportEndDate),
        // The batch these scores were written to, so the plan can store it and
        // resolve back to exactly this period when reopened.
        teamScoreBatchId: batchId,
        preselectedType: {
          value: "COLLABORATIVE",
          label: "Collaborative Care Plan",
        },
      },
    });
  };

  // Handle sign button click
  const handleSign = async () => {
    if (!data?.data) return;

    if (!hasTeamScoresBeenModified) {
      // Nothing was scored here, but the medians could fill the gaps — offer
      // them before leaving, rather than starting the plan with blanks.
      if (hasMediansForMissingTeamScores) {
        setShowUseMediansModal(true);
        return;
      }
      goToCarePlanWithoutTeamScores();
      return;
    }

    // Otherwise show the confirmation modal (no validation required)
    setShowSignModal(true);
  };

  // Handle confirming the sign action
  const handleConfirmSign = async () => {
    if (!data?.data) return;

    // Get all subheadings from all headings, each carrying the scale its
    // heading is scored on.
    const allSubheadings = data.data.reduce((acc, heading) => {
      return acc.concat(
        (heading.subheadings || []).map((subheading) => ({
          ...subheading,
          scoringScale: heading.scoringScale,
        })),
      );
    }, []);

    const patientId = localStorage.getItem("patient");

    const success = await submitTeamScores(
      patientId,
      allSubheadings,
      reportStartDate,
      reportEndDate,
    );

    setShowSignModal(false);

    if (success) {
      setAllowNavigation(true); // Allow navigation after successful submission
      setHasTeamScoresBeenModified(false); // Clear modification flag
      sessionStorage.removeItem("hasUnsavedTeamScores"); // Clear sessionStorage flag
      toast.success(
        <p className="text-center">
          Team Scores have been saved. <br /> Ready to CREATE Care Plan.
        </p>,
        {
          position: "top-right",
          autoClose: false,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          style: { backgroundColor: "#353C51", color: "white" },
          theme: "colored",
          transition: Bounce,
        },
      );

      const formattedFromDate = formatDateForAPI(reportStartDate);
      const formattedToDate = formatDateForAPI(reportEndDate);

      // Navigate to care management page with collaborative care plan preselected
      setTimeout(() => {
        props.history.push({
          pathname: "/hcp/patient/care_management",
          state: {
            fromTeamScores: true,
            date: new Date(),
            fromDate: formattedFromDate,
            toDate: formattedToDate,
            preselectedType: {
              value: "COLLABORATIVE",
              label: "Collaborative Care Plan",
            },
          },
        });
      }, 2000); // Wait 2 seconds to allow user to see the toast
    } else {
      toast.error(
        "An error occurred while submitting scores. Please try again.",
      );
    }
  };

  // Handle canceling the sign action
  const handleCancelSign = () => {
    setShowSignModal(false);
  };

  // Determine if sign button should be shown
  const shouldShowSignButton = () => {
    // Always show sign button when there's data (no validation required)
    return data?.data && data.data.length > 0;
  };

  // Handle provider selection
  const handleProviderSelection = (providerId, isChecked) => {
    if (isChecked) {
      setSelectedProviders([...selectedProviders, providerId]);
    } else {
      setSelectedProviders(selectedProviders.filter((id) => id !== providerId));
    }
  };

  // Handle search button click
  const handleSearch = () => {
    const formattedStartDate = formatDateForAPI(startDate);
    const formattedEndDate = formatDateForAPI(endDate);

    setFilterStartDate(formattedStartDate);
    setFilterEndDate(formattedEndDate);
    // Keep the report range in step with what is about to be displayed — it is
    // the range team scores get saved against.
    setReportStartDate(startDate);
    setReportEndDate(endDate);

    // Set score filter based on switch state and selected score
    if (checkedScores.check && selectedScore !== null) {
      // Transform selected score to include all scores within range
      setFilterScores(buildScoreRange(selectedScore));
    } else {
      setFilterScores(null);
    }

    // Set provider filter based on switch state and selected providers
    if (checkedNames.check && selectedProviders?.length > 0) {
      setFilterProviders(selectedProviders);
    } else {
      setFilterProviders(null);
    }

    // Set functional issues filter based on switch state and selected functional issues
    if (checked.check && selectedFunctionalIssues?.length > 0) {
      setFilterFunctionalIssues(selectedFunctionalIssues);
    } else {
      setFilterFunctionalIssues(null);
    }

    // Close the modal
    window.$("#filterModal").modal("hide");
  };

  // Clear filters
  const handleClearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setFilterStartDate(null);
    setFilterEndDate(null);
    setReportStartDate(null);
    setReportEndDate(null);
    setSelectedScore(null);
    setFilterScores(null);
    setSelectedProviders([]);
    setFilterProviders(null);
    setSelectedFunctionalIssues([]);
    setFilterFunctionalIssues(null);
    setChecked({ check: false, attr: "" });
    setCheckedScores({ check: false, attr: "" });
    setCheckedNames({ check: false, attr: "" });
  };

  // Export to PDF function
  // Open the template chooser popup when Download is clicked.
  // An assessment can have answered questions with every score filtered out, so
  // "nothing to export" means neither scores nor answers.
  const hasExportableData = () =>
    (data?.data?.length || 0) > 0 || questionsData.length > 0;

  const handleDownloadClick = () => {
    if (!hasExportableData()) {
      toast.info("No data available to export");
      return;
    }
    setShowDownloadModal(true);
  };

  // Export the PDF. mode "full" prints every contributor score; mode "summary"
  // prints only the subheading aggregates (median / team score), no individual
  // scores.
  const handleExportPDF = async (mode = "full") => {
    const isSummary = mode === "summary";
    setShowDownloadModal(false);
    if (!hasExportableData()) {
      toast.info("No data available to export");
      return;
    }

    let pdfAssets;
    try {
      pdfAssets = await loadPdfAssets();
    } catch {
      toast.error("Unable to prepare the PDF for download");
      return;
    }

    // Switch to portrait orientation
    const doc = new jsPDF("p", "mm", "a4");
    doc.addFileToVFS("Quicksand.ttf", pdfAssets.quicksandFontBase64);
    doc.addFont("Quicksand.ttf", "Quicksand", "normal");
    doc.setFont("Quicksand", "normal");

    // Scale factor for better fit
    const scale = 0.75;
    const pageWidth = doc.internal.pageSize.width;

    // Get patient data
    const patientData = JSON.parse(localStorage.getItem("localPatient") || "{}");

    addPdfHeader(
      doc,
      pdfAssets.logoDataUrl,
      patientData,
      reportStartDate,
      reportEndDate,
    );

    let yPosition = 25;

    // Process each heading
    (data?.data || []).forEach((heading, headingIndex) => {
      // Check if we need a new page (only when absolutely necessary)
      if (yPosition > 280 * scale) {
        doc.addPage();
        resetPdfPage(doc);
        yPosition = 20 * scale;
      }

      const marginX = 7;
      const headingBarHeight = 10;
      const scoreValues = getScaleValues(heading.scoringScale);
      const fullTableBaseWidth =
        40 + 25 + 40 + 35 + scoreValues.length * 15 + 40 + 20 + 25;
      // The original table widths were scaled for 15 mm margins. Keep the
      // same column proportions while expanding them to the new 7 mm header
      // margins so the table edge aligns with the heading bar.
      const tableColumnScale =
        (pageWidth - 2 * marginX) / (isSummary ? 240 : fullTableBaseWidth);

      // Heading bar with accent color #5D8FA8
      doc.setFillColor(93, 143, 168); // #5D8FA8
      doc.rect(
        marginX,
        yPosition,
        pageWidth - 2 * marginX,
        headingBarHeight,
        "F",
      );
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11 * scale);
      doc.setFont("Quicksand", "normal");
      doc.text(heading.headingName, marginX + 8 * scale, yPosition + 8 * scale);
      doc.setTextColor(0, 0, 0); // Reset to black
      yPosition += headingBarHeight + 1 * scale;

      // Summary template: one table per heading — the column titles are shown
      // once (on the heading), then one row per subheading with its median /
      // team score. No individual contributor scores.
      if (isSummary) {
        const summaryRows = heading.subheadings
          .filter((subheading) => subheading?.collaboratorScores?.length > 0)
          .map((subheading) => [
            subheading.subheadingName,
            normalizeScore(
              Math.floor(subheading.median),
              heading.scoringScale,
            ),
            normalizeScore(subheading.teamScore, heading.scoringScale),
          ]);

        if (summaryRows.length > 0) {
          if (yPosition > 260 * scale) {
            doc.addPage();
            resetPdfPage(doc);
            yPosition = 20 * scale;
          }

          doc.autoTable({
            head: [["Functional Issue", "Median", "Team score"]],
            body: summaryRows,
            theme: "grid",
            headStyles: {
              fillColor: [175, 177, 184], // #AFB1B8
              textColor: [255, 255, 255],
              fontSize: 11 * scale,
              fontStyle: "normal",
              font: "Quicksand",
              halign: "center",
              valign: "middle",
              cellPadding: 2.5 * scale,
            },
            bodyStyles: {
              font: "Quicksand",
              fontSize: 11 * scale,
              cellPadding: 2 * scale,
              halign: "center",
              valign: "middle",
            },
            columnStyles: {
              0: { cellWidth: 180 * tableColumnScale, halign: "left" },
              1: { cellWidth: 30 * tableColumnScale },
              2: { cellWidth: 30 * tableColumnScale },
            },
            margin: { left: marginX, right: marginX },
            pageBreak: "auto",
            showHead: "everyPage",
            startY: yPosition,
            didParseCell: function (data) {
              if (data.row.index === 0 && data.row.section === "head") {
                data.cell.styles.fillColor = [175, 177, 184]; // #AFB1B8
                data.cell.styles.textColor = [255, 255, 255];
                data.cell.styles.lineWidth = 0.2;
              }
              if (data.row.section === "body") {
                data.cell.styles.fillColor = [233, 246, 245]; // #EAF6F5
                data.cell.styles.textColor = 0;
                data.cell.styles.cellPadding = 3 * scale;
                data.cell.styles.lineWidth = 0;
                if (
                  [1, 2].includes(data.column.index) &&
                  getNumericScore(data.cell.raw, heading.scoringScale) !== null
                ) {
                  data.cell.text = [""];
                }
              }
            },
            didDrawCell: function (data) {
              if (data.row.section === "body") {
                const cell = data.cell;
                const doc = data.doc;
                doc.setDrawColor(255, 255, 255);
                doc.setLineWidth(0.1);
                doc.rect(cell.x, cell.y, cell.width, cell.height, "S");
                if ([1, 2].includes(data.column.index)) {
                  drawPdfScoreCircle(
                    doc,
                    cell,
                    data.cell.raw,
                    scale,
                    heading.scoringScale,
                  );
                }
              }
            },
          });

          yPosition = doc.lastAutoTable
            ? doc.lastAutoTable.finalY + 15 * scale
            : yPosition + 10 * scale;
        }

        return; // Heading fully rendered for the summary template
      }

      // Process subheadings
      heading.subheadings.forEach((subheading) => {
        if (subheading?.collaboratorScores?.length > 0) {
          // Let tables use full page space - only break if absolutely necessary
          if (yPosition > 260 * scale) {
            doc.addPage();
            resetPdfPage(doc);
            yPosition = 20 * scale;
          }

          // Prepare table data with subheading as title row and indented provider entries
          const tableData = [];
          const scoreStartColumn = 4;
          const scoreEndColumn = scoreStartColumn + scoreValues.length;
          const commentsColumn = scoreEndColumn;
          const medianColumn = commentsColumn + 1;
          const teamScoreColumn = medianColumn + 1;

          // Add subheading as title row (first row)
          tableData.push([
            subheading.subheadingName,
            "", // Empty position
            "", // Empty provider
            "", // Empty assessment date
            ...scoreValues.map(() => ""), // Empty individual score columns
            "", // Empty comments
            normalizeScore(
              Math.floor(subheading.median),
              heading.scoringScale,
            ),
            normalizeScore(subheading.teamScore, heading.scoringScale),
          ]);

          // Add provider entries (indented) - unanswered items are left out,
          // N/A ones are listed with a blank score
          subheading.collaboratorScores
            .filter((score) => hasScore(score, heading.scoringScale))
            .forEach((score) => {
              const numericScore = getNumericScore(
                score.score,
                heading.scoringScale,
              );
              tableData.push([
                "", // Empty functional issue for provider rows (matching print)
                score.position?.abbreviation || "N/A",
                `${score.lastname}, ${score.firstname}`,
                formatInEt(score.assessmentDate, "D MMM YYYY"),
                ...scoreValues.map((value) =>
                  numericScore === value ? numericScore : "",
                ),
                score.comment || "N/A",
                "", // Empty median for provider rows
                "", // Empty team score for provider rows
              ]);
            });

          // Create table
          doc.autoTable({
            head: [
              [
                "Functional Issue",
                "Position",
                "Provider",
                "Assessment Date",
                ...scoreValues,
                "Comments",
                "Median",
                "Team score",
              ],
            ],
            body: tableData,
            theme: "grid",
            headStyles: {
              fillColor: [175, 177, 184], // #AFB1B8 - matching week_conference
              textColor: [255, 255, 255],
              fontSize: 11 * scale,
              fontStyle: "normal",
              font: "Quicksand",
              halign: "center",
              valign: "middle",
              cellPadding: 2.5 * scale,
            },
            bodyStyles: {
              font: "Quicksand",
              fontSize: 11 * scale,
              cellPadding: 2 * scale,
              halign: "center",
              valign: "middle",
            },
            columnStyles: {
              0: { cellWidth: 40 * tableColumnScale, halign: "left" },
              1: { cellWidth: 25 * tableColumnScale },
              2: { cellWidth: 40 * tableColumnScale, halign: "left" },
              3: { cellWidth: 35 * tableColumnScale },
              ...scoreValues.reduce(
                (styles, value, index) => ({
                  ...styles,
                  [scoreStartColumn + index]: {
                    cellWidth: 15 * tableColumnScale,
                  },
                }),
                {},
              ),
              [commentsColumn]: {
                cellWidth: 40 * tableColumnScale,
                halign: "left",
              },
              [medianColumn]: { cellWidth: 20 * tableColumnScale },
              [teamScoreColumn]: { cellWidth: 25 * tableColumnScale },
            },
            margin: { left: marginX, right: marginX },
            pageBreak: "auto",
            showHead: "everyPage",
            startY: yPosition,
            didParseCell: function (data) {
              // Style header row
              if (data.row.index === 0 && data.row.section === "head") {
                data.cell.styles.fillColor = [175, 177, 184]; // #AFB1B8
                data.cell.styles.textColor = [255, 255, 255];
                data.cell.styles.lineWidth = 0.2;
              }

              // Style body rows
              if (data.row.section === "body") {
                // Check if this is a title row (first data row) or provider row
                const firstCellText =
                  tableData[data.row.index] && tableData[data.row.index][0];
                const isTitleRow = firstCellText && firstCellText !== "";
                const isScoreCell =
                  (!isTitleRow &&
                    data.column.index >= scoreStartColumn &&
                    data.column.index < scoreEndColumn) ||
                  (isTitleRow &&
                    [medianColumn, teamScoreColumn].includes(data.column.index));

                if (isTitleRow) {
                  // Title row - bold text, light teal background (matching print)
                  data.cell.styles.fillColor = [233, 246, 245]; // #EAF6F5 - light teal
                  data.cell.styles.textColor = 0;
                  data.cell.styles.cellPadding = 3 * scale;
                  data.cell.styles.lineWidth = 0;
                } else {
                  // Provider rows - light gray background (matching print)
                  // Keep the lead cell white like the on-screen table; it is
                  // the visual indentation before Position, not a grey data
                  // column.
                  data.cell.styles.fillColor =
                    data.column.index === 0
                      ? [255, 255, 255]
                      : [245, 245, 245]; // #F5F5F5 - light gray
                  data.cell.styles.textColor = 0;
                  data.cell.styles.cellPadding = 3 * scale;
                  data.cell.styles.lineWidth = 0;
                }

                if (
                  isScoreCell &&
                  getNumericScore(data.cell.raw, heading.scoringScale) !== null
                ) {
                  data.cell.text = [""];
                }
              }
            },
            didDrawCell: function (data) {
              // Draw borders like week_conference
              if (data.row.section === "body") {
                const cell = data.cell;
                const doc = data.doc;
                const firstCellText =
                  tableData[data.row.index] && tableData[data.row.index][0];
                const isTitleRow = firstCellText && firstCellText !== "";
                const isScoreCell =
                  (!isTitleRow &&
                    data.column.index >= scoreStartColumn &&
                    data.column.index < scoreEndColumn) ||
                  (isTitleRow &&
                    [medianColumn, teamScoreColumn].includes(data.column.index));

                // Draw white borders
                doc.setDrawColor(255, 255, 255);
                doc.setLineWidth(0.1);
                doc.rect(cell.x, cell.y, cell.width, cell.height, "S");
                if (isScoreCell) {
                  drawPdfScoreCircle(
                    doc,
                    cell,
                    data.cell.raw,
                    scale,
                    heading.scoringScale,
                  );
                }
              }
            },
          });

          yPosition = doc.lastAutoTable
            ? doc.lastAutoTable.finalY
            : yPosition + 10 * scale;
        }
      });
    });

    // Questions, once at the end: an answer belongs to the assessment rather
    // than to any heading, so it has no place inside the per-heading tables.
    // Left out entirely when nobody answered.
    if (questionsData.length > 0) {
      if (yPosition > 250 * scale) {
        doc.addPage();
        doc.setFillColor(255, 255, 255);
        doc.rect(
          0,
          0,
          doc.internal.pageSize.width,
          doc.internal.pageSize.height,
          "F",
        );
        yPosition = 20 * scale;
      }

      const marginX = 20 * scale;
      const headingBarHeight = 12 * scale;
      doc.setFillColor(93, 143, 168); // #5D8FA8
      doc.rect(
        marginX,
        yPosition,
        pageWidth - 2 * marginX,
        headingBarHeight,
        "F",
      );
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11 * scale);
      doc.setFont(undefined, "normal");
      doc.text("Questions", marginX + 8 * scale, yPosition + 8 * scale);
      doc.setTextColor(0, 0, 0);
      yPosition += headingBarHeight + 1 * scale;

      // One row per question, then one indented row per answer — the same
      // title-row/detail-row shape the score tables use.
      const questionRows = [];
      const questionTitleRowIndexes = new Set();
      questionsData.forEach((question) => {
        questionTitleRowIndexes.add(questionRows.length);
        questionRows.push([question.questionText, "", "", ""]);
        question.responses.forEach((response) => {
          questionRows.push([
            "",
            response.position?.abbreviation || "N/A",
            `${response.lastname || ""}, ${response.firstname || ""}`,
            response.answer || "",
          ]);
        });
      });

      doc.autoTable({
        head: [["Question", "Position", "Provider", "Answer"]],
        body: questionRows,
        theme: "grid",
        headStyles: {
          fillColor: [175, 177, 184], // #AFB1B8
          textColor: [255, 255, 255],
          fontSize: 11 * scale,
          fontStyle: "normal",
          halign: "center",
          valign: "middle",
          cellPadding: 2.5 * scale,
        },
        bodyStyles: {
          fontSize: 10 * scale,
          cellPadding: 2 * scale,
          valign: "middle",
        },
        columnStyles: {
          0: { cellWidth: 45 * scale, halign: "left" },
          1: { cellWidth: 20 * scale, halign: "center" },
          2: { cellWidth: 35 * scale, halign: "center" },
          3: { cellWidth: "auto", halign: "left" },
        },
        margin: { left: marginX, right: marginX },
        pageBreak: "auto",
        showHead: "everyPage",
        startY: yPosition,
        didParseCell: function (cellData) {
          if (cellData.row.section !== "body") return;
          if (questionTitleRowIndexes.has(cellData.row.index)) {
            cellData.cell.styles.fillColor = [233, 246, 245]; // #EAF6F5
            cellData.cell.styles.fontStyle = "bold";
          } else {
            cellData.cell.styles.fillColor = [245, 245, 245]; // #F5F5F5
          }
          cellData.cell.styles.textColor = 0;
          cellData.cell.styles.lineWidth = 0;
        },
        didDrawCell: function (cellData) {
          if (cellData.row.section !== "body") return;
          const cell = cellData.cell;
          cellData.doc.setDrawColor(255, 255, 255);
          cellData.doc.setLineWidth(0.1);
          cellData.doc.rect(cell.x, cell.y, cell.width, cell.height, "S");
        },
      });

      yPosition = doc.lastAutoTable
        ? doc.lastAutoTable.finalY + 15 * scale
        : yPosition + 10 * scale;
    }

    // Save the PDF
    const patientName = localStorage.getItem("patientName") || "Patient";
    const templateLabel = isSummary ? "Summary" : "Full";
    const fileName = `Functional_Summaries_${templateLabel}_${patientName}_${moment().format(
      "YYYY-MM-DD",
    )}.pdf`;
    doc.save(fileName);
  };

  const handleStartDateChange = (date) => {
    setStartDate(normalizePickerDate(date));
  };

  const handleEndDateChange = (date) => {
    setEndDate(normalizePickerDate(date));
  };

  const normalizePickerDate = (date) => {
    if (!date) return null;
    return moment(date).startOf("day").toDate();
  };

  const toggleFilterDropdown = (id) => {
    setOpenFilterDropdown(openFilterDropdown === id ? null : id);
  };

  const selectDraftPreset = (days) => {
    const end = moment().startOf("day");
    const start = moment(end).subtract(days - 1, "days");
    setDraftStartDate(start.toDate());
    setDraftEndDate(end.toDate());
    setDraftSingleDate(days === 1 ? end.toDate() : null);
  };

  const buildScoreRange = (score) => {
    if (!score) return null;
    const expandedScores = [];
    for (let i = 1; i <= Number(score); i++) {
      expandedScores.push(i);
    }
    return expandedScores;
  };

  const handleApplyTimeDropdown = () => {
    const formattedStartDate = formatDateForAPI(draftStartDate);
    const formattedEndDate = formatDateForAPI(draftEndDate);

    setStartDate(draftStartDate);
    setEndDate(draftEndDate);
    setReportStartDate(draftStartDate);
    setReportEndDate(draftEndDate);
    setFilterStartDate(formattedStartDate);
    setFilterEndDate(formattedEndDate);
    setOpenFilterDropdown(null);
  };

  // Single-date selection: search records for one day (from = to = that date)
  const handleDraftSingleDateChange = (date) => {
    const normalized = normalizePickerDate(date);
    setDraftSingleDate(normalized);
    setDraftStartDate(normalized);
    setDraftEndDate(normalized);
  };

  const handleClearTimeDropdown = () => {
    setDraftStartDate(null);
    setDraftEndDate(null);
    setDraftSingleDate(null);
    setStartDate(null);
    setEndDate(null);
    setReportStartDate(null);
    setReportEndDate(null);
    setFilterStartDate(null);
    setFilterEndDate(null);
    setOpenFilterDropdown(null);
  };

  const toggleDraftFunctionalIssue = (issueId) => {
    setDraftFunctionalIssues((currentIssues) =>
      currentIssues.includes(issueId)
        ? currentIssues.filter((id) => id !== issueId)
        : [...currentIssues, issueId],
    );
  };

  const toggleExpandedFilterHeading = (headingId) => {
    setExpandedFilterHeadingIds((currentHeadings) =>
      currentHeadings.includes(headingId)
        ? currentHeadings.filter((id) => id !== headingId)
        : [...currentHeadings, headingId],
    );
  };

  const handleApplyFunctionalIssuesDropdown = () => {
    setSelectedFunctionalIssues(draftFunctionalIssues);
    setFilterFunctionalIssues(
      draftFunctionalIssues.length > 0 ? draftFunctionalIssues : null,
    );
    setChecked({
      check: draftFunctionalIssues.length > 0,
      attr: draftFunctionalIssues.length > 0 ? "function" : "",
    });
    setOpenFilterDropdown(null);
  };

  const handleClearFunctionalIssuesDropdown = () => {
    setDraftFunctionalIssues([]);
    setSelectedFunctionalIssues([]);
    setFilterFunctionalIssues(null);
    setChecked({ check: false, attr: "" });
    setOpenFilterDropdown(null);
  };

  const handleApplyScoreDropdown = () => {
    // 0 is a real score on an ICF heading, so "nothing picked" is null rather
    // than falsy.
    const hasPick = draftScore !== null && draftScore !== undefined && draftScore !== "";
    const nextScore = hasPick ? Number(draftScore) : null;
    setSelectedScore(nextScore);
    setFilterScores(hasPick ? buildScoreRange(nextScore) : null);
    setCheckedScores({
      check: hasPick,
      attr: hasPick ? "score" : "",
    });
    setOpenFilterDropdown(null);
  };

  const handleClearScoreDropdown = () => {
    setDraftScore(null);
    setSelectedScore(null);
    setFilterScores(null);
    setCheckedScores({ check: false, attr: "" });
    setOpenFilterDropdown(null);
  };

  const toggleDraftProvider = (providerId) => {
    setDraftProviders((currentProviders) =>
      currentProviders.includes(providerId)
        ? currentProviders.filter((id) => id !== providerId)
        : [...currentProviders, providerId],
    );
  };

  const handleApplyProvidersDropdown = () => {
    setSelectedProviders(draftProviders);
    setFilterProviders(draftProviders.length > 0 ? draftProviders : null);
    setCheckedNames({
      check: draftProviders.length > 0,
      attr: draftProviders.length > 0 ? "names" : "",
    });
    setOpenFilterDropdown(null);
  };

  const handleClearProvidersDropdown = () => {
    setDraftProviders([]);
    setSelectedProviders([]);
    setFilterProviders(null);
    setCheckedNames({ check: false, attr: "" });
    setOpenFilterDropdown(null);
  };

  const handleResetLozengeFilters = () => {
    const defaultTimePeriod = getDefaultTimePeriod();
    setDraftStartDate(defaultTimePeriod.startDate);
    setDraftEndDate(defaultTimePeriod.endDate);
    setDraftSingleDate(null);
    setStartDate(defaultTimePeriod.startDate);
    setEndDate(defaultTimePeriod.endDate);
    setReportStartDate(defaultTimePeriod.startDate);
    setReportEndDate(defaultTimePeriod.endDate);
    setFilterStartDate(defaultTimePeriod.formattedStartDate);
    setFilterEndDate(defaultTimePeriod.formattedEndDate);
    setDraftFunctionalIssues([]);
    setSelectedFunctionalIssues([]);
    setFilterFunctionalIssues(null);
    setChecked({ check: false, attr: "" });
    setDraftScore(null);
    setSelectedScore(null);
    setFilterScores(null);
    setCheckedScores({ check: false, attr: "" });
    setDraftProviders([]);
    setSelectedProviders([]);
    setFilterProviders(null);
    setCheckedNames({ check: false, attr: "" });
    setOpenFilterDropdown(null);
  };

  const hasActiveLozengeFilters = () => {
    const defaultTimePeriod = getDefaultTimePeriod();
    const hasCustomTimePeriod =
      filterStartDate !== defaultTimePeriod.formattedStartDate ||
      filterEndDate !== defaultTimePeriod.formattedEndDate;

    return (
      hasCustomTimePeriod ||
      Boolean(filterFunctionalIssues?.length) ||
      Boolean(filterScores?.length) ||
      Boolean(filterProviders?.length)
    );
  };

  const formatFilterDateRangeDisplay = () => {
    const displayStartDate = filterStartDate || startDate;
    const displayEndDate = filterEndDate || endDate;

    if (
      !displayStartDate ||
      !displayEndDate ||
      !moment(displayStartDate).isValid() ||
      !moment(displayEndDate).isValid()
    ) {
      return "No time period selected";
    }

    const startMoment = moment(displayStartDate);
    const endMoment = moment(displayEndDate);

    if (startMoment.isSame(endMoment, "day")) {
      return startMoment.format("D MMM YYYY");
    }

    return `${startMoment.format("D MMM YYYY")} - ${endMoment.format(
      "D MMM YYYY",
    )}`;
  };

  const functionalIssueFilterCount = filterFunctionalIssues?.length || 0;
  const providerFilterCount = filterProviders?.length || 0;
  const scoreFilterLabel =
    filterScores?.length > 0
      ? `Score ${
          filterScores.length === 1
            ? filterScores[0]
            : `<= ${Math.max(...filterScores)}`
        }`
      : "Score";
  const functionalFilterHeadings = functionalIssues?.data?.[0]?.headings || [];

  // The score filter spans the whole checklist rather than one heading, so it
  // offers every value any heading on it can be scored on — the union of the
  // scales in play, which is just 1-5 when they are all POMMe-Health.
  const scoreFilterValues = Array.from(
    new Set(
      functionalFilterHeadings.flatMap((heading) =>
        getScaleValues(heading.scoringScale),
      ),
    ),
  ).sort((a, b) => a - b);
  const scoreOptions = (
    scoreFilterValues.length ? scoreFilterValues : getScaleValues()
  ).map((value, index) => ({
    // The lowest value stands for itself; every higher one filters to "this or
    // anything below it".
    label: index === 0 ? String(value) : `\u2264${value}`,
    value,
  }));

  const headings = data && !isLoading ? data.data : [];

  // Logged-in user's provider id (matches score.providerId on summary entries)
  const currentProviderId = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("user"))?.organizations?.[0]
          ?.careProvider?.id ?? null
      );
    } catch {
      return null;
    }
  })();

  // Every provider represented in the data currently on screen. Not filtered by
  // `hasScore`: a contributor with an all-blank batch still counts as a second
  // person in the record, which is the safe direction for the check below.
  const displayedProviderIds = (() => {
    const ids = new Set();
    for (const heading of data?.data || []) {
      for (const subheading of heading.subheadings || []) {
        for (const score of subheading.collaboratorScores || []) {
          if (score.providerId) ids.add(score.providerId);
        }
      }
    }
    return ids;
  })();

  // The provider whose batches may be amended, or null. "One user, one day" can
  // be satisfied two ways: the user explicitly filtered to just themselves, or
  // the filtered result set simply contains nobody else — in which case they
  // shouldn't have to tick their own name to prove it. The date range itself is
  // whatever the filters say; a range is as valid as a single day, because a
  // range holding several of the user's own batches still resolves through the
  // batch picker below.
  const amendableProviderId = (() => {
    if (!currentProviderId) return null;
    if (filterProviders?.length) {
      return filterProviders.length === 1 &&
        filterProviders[0] === currentProviderId
        ? currentProviderId
        : null;
    }
    return displayedProviderIds.size === 1 &&
      displayedProviderIds.has(currentProviderId)
      ? currentProviderId
      : null;
  })();

  // The calendar day of an assessment date, taken verbatim off the string.
  // `assessmentDate` is a zone-less local date-time, so slicing the date out
  // beats parsing it — no viewer timezone can shift the day that way.
  const assessmentDayKey = (value) =>
    typeof value === "string" ? value.slice(0, 10) : "";

  // There may be several signed batches in range. Collect each distinct CURRENT
  // batch (deduped by entryId) so the user can pick which one to amend. Previous
  // versions are excluded — you always amend the current version of a lineage.
  const editableBatches = (() => {
    if (!amendableProviderId) return [];
    if (!data?.data?.length) return [];

    const byEntry = new Map();
    for (const heading of data.data) {
      for (const subheading of heading.subheadings || []) {
        for (const score of subheading.collaboratorScores || []) {
          if (
            score.providerId === amendableProviderId &&
            score.currentVersion &&
            score.entryId &&
            !byEntry.has(score.entryId)
          ) {
            byEntry.set(score.entryId, {
              entryId: score.entryId,
              rootId: score.rootId,
              versionNumber: score.versionNumber,
              assessmentDay: assessmentDayKey(score.assessmentDate),
              createdAt:
                score.dateCreated || score.signedAt || score.assessmentDate,
              // dateCreated and signedAt are UTC instants, so they render as a
              // true Eastern time. assessmentDate is a zone-less wall clock and
              // carries no instant — falling back to it means we can show the
              // date but must not claim a time.
              createdAtIsInstant: Boolean(score.dateCreated || score.signedAt),
            });
          }
        }
      }
    }
    return Array.from(byEntry.values()).sort(
      (a, b) => moment(a.createdAt).valueOf() - moment(b.createdAt).valueOf(),
    );
  })();

  // Several batches are only ever picked between when they cover the same
  // assessment date — records of the same day's assessment, which the date
  // filter cannot separate. The picker then tells them apart by when each was
  // created. Batches on different assessment dates are already distinguished
  // by the filter, so there is nothing to choose between.
  const editableBatchDays = new Set(
    editableBatches.map((batch) => batch.assessmentDay),
  );
  const batchesSpanMultipleDays =
    editableBatches.length > 1 && editableBatchDays.size > 1;

  // The batch we'll actually amend. With a single current batch we edit it
  // directly (no selection needed); with two or more on one day, the user must
  // check one and that choice must still be valid for the current filters.
  // Batches spread across different days are never amendable — narrow the
  // filter first.
  const effectiveBatchEntryId = batchesSpanMultipleDays
    ? null
    : editableBatches.length === 1
      ? editableBatches[0].entryId
      : editableBatches.some((b) => b.entryId === selectedBatchEntryId)
        ? selectedBatchEntryId
        : null;

  const canEditBatch = Boolean(effectiveBatchEntryId);

  // A selection made under an earlier filter must not outlive the picker —
  // `selectedLineage` reads it for the edit-reason history, which would
  // otherwise describe a record the user can no longer see or pick.
  useEffect(() => {
    if (batchesSpanMultipleDays && selectedBatchEntryId) {
      setSelectedBatchEntryId(null);
    }
  }, [batchesSpanMultipleDays, selectedBatchEntryId]);

  // Version lineages present in the currently displayed scores, grouped by
  // rootId. Each carries its amended-version reasons (newest first) and the set
  // of entryIds that belong to the lineage (so a selected batch maps to it).
  // This is independent of the amend-only editableBatches filter, so reasons
  // show whenever the data is viewed — not just while amending.
  const displayedLineages = (() => {
    if (!data?.data?.length) return [];
    const byRoot = new Map();
    for (const heading of data.data) {
      for (const subheading of heading.subheadings || []) {
        for (const score of subheading.collaboratorScores || []) {
          const root = score.rootId;
          if (!root) continue;
          if (!byRoot.has(root)) {
            byRoot.set(root, {
              rootId: root,
              entryIds: new Set(),
              reasonsByVersion: new Map(),
            });
          }
          const lineage = byRoot.get(root);
          if (score.entryId) lineage.entryIds.add(score.entryId);
          // v1 has no editReason so it drops out; dedupe by version number
          // since the lineage can span subheadings.
          if (
            score.editReason &&
            !lineage.reasonsByVersion.has(score.versionNumber)
          ) {
            lineage.reasonsByVersion.set(score.versionNumber, {
              versionNumber: score.versionNumber,
              editReason: score.editReason,
            });
          }
        }
      }
    }
    return Array.from(byRoot.values()).map((lineage) => ({
      rootId: lineage.rootId,
      entryIds: lineage.entryIds,
      reasons: Array.from(lineage.reasonsByVersion.values()).sort(
        (a, b) => b.versionNumber - a.versionNumber,
      ),
    }));
  })();

  // The lineage whose reasons we display: the selected batch's lineage, or —
  // when the filtered data resolves to a single lineage — that one.
  const selectedLineage =
    (selectedBatchEntryId &&
      displayedLineages.find((l) => l.entryIds.has(selectedBatchEntryId))) ||
    (displayedLineages.length === 1 ? displayedLineages[0] : null);

  // Read-only edit-reason history (V2, V3, …) for the selected lineage.
  const lineageReasons = selectedLineage?.reasons || [];

  // Open the batch in the review/edit flow (same path the outstanding documents
  // list uses). The unsaved-team-scores Prompt still guards this.
  const handleEditBatch = () => {
    if (!effectiveBatchEntryId) return;
    localStorage.setItem("documentId", effectiveBatchEntryId);
    props.history.push({
      pathname: "/hcp/patient/function_review_entry",
      state: { amend: true, entryId: effectiveBatchEntryId },
    });
  };

  return (
    <>
      {hasTeamScoresBeenModified && (
        <Prompt when={!allowNavigation} message={handleNavigationAttempt} />
      )}
      <div className="container-fluid pt-12 pl-21 pb-12 pr-24 h-100 noScroll">
        <div className="card border-0 h-100">
          <div className="card-body pb-0 d-flex flex-column">
            <div className="d-flex justify-content-between align-items-center flex-wrap">
              <div className="functional-summary-card-title-row">
                <h5 className="font-weight-lighter mb-0">
                  HCP: VIEW Functional Summaries :
                </h5>
                <span className="functional-summary-card-period">
                  {formatFilterDateRangeDisplay()}
                </span>
              </div>
              <div className="d-flex align-items-center">
                {headings?.length > 0 && (
                  <Link
                    className="btn btn-primary p-2 rounded"
                    to={{
                      pathname: "/hcp/patient/summary_functions_graph",
                      state: {
                        graphFilters: {
                          startDate: startDate
                            ? formatDateForAPI(startDate)
                            : null,
                          endDate: endDate ? formatDateForAPI(endDate) : null,
                          filterStartDate,
                          filterEndDate,
                          selectedScore,
                          filterScores,
                          selectedProviders,
                          checkedNames,
                          selectedFunctionalIssues,
                          filterFunctionalIssues,
                          checked,
                          checkedScores,
                          filterProviders,
                        },
                      },
                    }}
                  >
                    <GoGraph size={19} />
                    &nbsp;{" "}
                    <small className="font-weight-bold">Go to Graph</small>
                    &nbsp; <IoChevronForwardSharp size={21} />
                  </Link>
                )}
              </div>
            </div>
            <hr />

            <div className="pb-17 functional-filter-block">
              <div className="functional-filter-bar" ref={filterBarRef}>
                <span className="functional-filter-bar-label">Filter:</span>
                <FilterDropdown
                  id="time"
                  label="Time Period"
                  className="functional-filter-lozenge-time"
                  panelClassName="functional-filter-menu-time"
                  openDropdown={openFilterDropdown}
                  onToggle={toggleFilterDropdown}
                >
                  <div className="functional-filter-menu-content">
                    <div className="functional-filter-presets">
                      <button
                        type="button"
                        className="functional-filter-preset"
                        onClick={() => selectDraftPreset(1)}
                      >
                        Today
                      </button>
                      <button
                        type="button"
                        className="functional-filter-preset"
                        onClick={() => selectDraftPreset(7)}
                      >
                        Last 7 Days
                      </button>
                      <button
                        type="button"
                        className="functional-filter-preset"
                        onClick={() => selectDraftPreset(30)}
                      >
                        Last 30 Days
                      </button>
                    </div>

                    <div className="functional-filter-section-title">
                      Custom Date
                    </div>
                    <hr className="functional-filter-divider" />

                    <div className="functional-filter-date-grid">
                      <div>
                        <small className="functional-filter-field-label">
                          Start Date
                        </small>
                        <PickDate
                          date={draftStartDate}
                          setDate={(date) => {
                            setDraftStartDate(normalizePickerDate(date));
                            setDraftSingleDate(null);
                          }}
                          required={false}
                        />
                      </div>
                      <div>
                        <small className="functional-filter-field-label">
                          End Date
                        </small>
                        <PickDate
                          date={draftEndDate}
                          setDate={(date) => {
                            setDraftEndDate(normalizePickerDate(date));
                            setDraftSingleDate(null);
                          }}
                          required={false}
                        />
                      </div>
                    </div>

                    <div className="functional-filter-actions">
                      <button
                        type="button"
                        className="functional-filter-action functional-filter-action-clear"
                        onClick={handleClearTimeDropdown}
                      >
                        CLEAR
                      </button>
                      <button
                        type="button"
                        className="functional-filter-action functional-filter-action-apply"
                        onClick={handleApplyTimeDropdown}
                      >
                        APPLY
                      </button>
                    </div>
                  </div>
                </FilterDropdown>
                <FilterDropdown
                  id="issues"
                  label="Functional Issues"
                  count={functionalIssueFilterCount}
                  className="functional-filter-lozenge-issues"
                  panelClassName="functional-filter-menu-issues"
                  openDropdown={openFilterDropdown}
                  onToggle={toggleFilterDropdown}
                >
                  <div className="functional-filter-menu-content h-100">
                    <p className="functional-filter-menu-prompt">
                      Choose as many as applicable:
                    </p>

                    <div className="functional-filter-scroll">
                      {checklistLoading && (
                        <small className="text-muted">
                          Loading functional issues...
                        </small>
                      )}
                      {!checklistLoading &&
                        functionalFilterHeadings.map((heading) => {
                          const isExpanded = expandedFilterHeadingIds.includes(
                            heading.id,
                          );

                          return (
                            <div
                              key={heading.id}
                              className="functional-filter-heading"
                            >
                              <button
                                type="button"
                                className="functional-filter-heading-button"
                                onClick={() =>
                                  toggleExpandedFilterHeading(heading.id)
                                }
                              >
                                {isExpanded ? (
                                  <BsChevronUp size={18} />
                                ) : (
                                  <BsChevronDown size={18} />
                                )}
                                <span>{heading.name}</span>
                              </button>

                              {isExpanded && (
                                <div className="functional-filter-options">
                                  {heading.subheadings.map((item) => (
                                    <div
                                      key={item.id}
                                      className="functional-filter-check-row dropdown-item noFilterHover form-group form-check wa mb-2"
                                    >
                                      <input
                                        type="checkbox"
                                        className="form-check-input mt-2"
                                        id={`dropdownFunctionalIssueCheck${item.id}`}
                                        checked={draftFunctionalIssues.includes(
                                          item.id,
                                        )}
                                        onChange={() =>
                                          toggleDraftFunctionalIssue(item.id)
                                        }
                                      />
                                      <label
                                        className="form-check-label f-13 ml-2 mb-0"
                                        htmlFor={`dropdownFunctionalIssueCheck${item.id}`}
                                      >
                                        <small>{item.name}</small>
                                      </label>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      {!checklistLoading &&
                        functionalFilterHeadings.length === 0 && (
                          <small className="text-muted">
                            No functional issues available
                          </small>
                        )}
                    </div>

                    <div className="functional-filter-actions">
                      <button
                        type="button"
                        className="functional-filter-action functional-filter-action-clear"
                        onClick={handleClearFunctionalIssuesDropdown}
                      >
                        CLEAR
                      </button>
                      <button
                        type="button"
                        className="functional-filter-action functional-filter-action-apply"
                        onClick={handleApplyFunctionalIssuesDropdown}
                      >
                        APPLY
                      </button>
                    </div>
                  </div>
                </FilterDropdown>
                <FilterDropdown
                  id="score"
                  label={scoreFilterLabel}
                  className="functional-filter-lozenge-score"
                  panelClassName="functional-filter-menu-score"
                  openDropdown={openFilterDropdown}
                  onToggle={toggleFilterDropdown}
                >
                  <div className="functional-filter-menu-content">
                    <p className="functional-filter-menu-prompt">
                      Choose one score:
                    </p>
                    <div className="functional-filter-score-options">
                      {scoreOptions.map((score) => (
                        <div
                          key={score.value}
                          className="functional-filter-radio-row dropdown-item noFilterHover form-group form-check wa mb-1"
                        >
                          <input
                            type="radio"
                            className="form-check-input mt-2"
                            id={`dropdownScoreRadio${score.value}`}
                            name="functionalScoreFilter"
                            checked={draftScore === score.value}
                            onChange={() => setDraftScore(score.value)}
                          />
                          <label
                            className="form-check-label f-13 ml-2 mb-0"
                            htmlFor={`dropdownScoreRadio${score.value}`}
                          >
                            <small>{score.label}</small>
                          </label>
                        </div>
                      ))}
                    </div>
                    <div className="functional-filter-actions">
                      <button
                        type="button"
                        className="functional-filter-action functional-filter-action-clear"
                        onClick={handleClearScoreDropdown}
                      >
                        CLEAR
                      </button>
                      <button
                        type="button"
                        className="functional-filter-action functional-filter-action-apply"
                        onClick={handleApplyScoreDropdown}
                      >
                        APPLY
                      </button>
                    </div>
                  </div>
                </FilterDropdown>
                <FilterDropdown
                  id="providers"
                  label="Provider/ Patient/ Family"
                  count={providerFilterCount}
                  className="functional-filter-lozenge-provider"
                  panelClassName="functional-filter-menu-provider"
                  openDropdown={openFilterDropdown}
                  onToggle={toggleFilterDropdown}
                >
                  <div className="functional-filter-menu-content">
                    <p className="functional-filter-menu-prompt">
                      Choose as many as applicable:
                    </p>
                    <div className="functional-filter-scroll functional-filter-scroll-provider">
                      {availableProviders.map((provider) => (
                        <div
                          key={provider.id}
                          className="functional-filter-check-row dropdown-item noFilterHover form-group form-check wa mb-2"
                        >
                          <input
                            type="checkbox"
                            className="form-check-input mt-2"
                            id={`dropdownProviderCheck${provider.id}`}
                            checked={draftProviders.includes(provider.id)}
                            onChange={() => toggleDraftProvider(provider.id)}
                          />
                          <label
                            className="form-check-label f-13 ml-2 mb-0"
                            htmlFor={`dropdownProviderCheck${provider.id}`}
                          >
                            <small>{provider.fullName}</small>
                          </label>
                        </div>
                      ))}
                      {availableProviders.length === 0 && (
                        <small className="text-muted">
                          No providers found in current data
                        </small>
                      )}
                    </div>
                    <div className="functional-filter-actions">
                      <button
                        type="button"
                        className="functional-filter-action functional-filter-action-clear"
                        onClick={handleClearProvidersDropdown}
                      >
                        CLEAR
                      </button>
                      <button
                        type="button"
                        className="functional-filter-action functional-filter-action-apply"
                        onClick={handleApplyProvidersDropdown}
                      >
                        APPLY
                      </button>
                    </div>
                  </div>
                </FilterDropdown>
                {hasActiveLozengeFilters() && (
                  <button
                    type="button"
                    className="functional-filter-reset"
                    onClick={handleResetLozengeFilters}
                  >
                    Reset Filter
                  </button>
                )}
                {/* Always rendered — greyed out until the filters resolve to a
                    single amendable batch of the user's own. */}
                <button
                  type="button"
                  className="functional-filter-edit"
                  onClick={handleEditBatch}
                  disabled={!canEditBatch}
                  title={
                    canEditBatch
                      ? "Edit this record"
                      : batchesSpanMultipleDays
                        ? "Narrow the date filter to a single assessment date to edit a record"
                        : editableBatches.length > 1
                          ? "Select which record to edit"
                          : "Editing is available when the records shown are your own"
                  }
                >
                  EDIT
                </button>
              </div>
              {/* States the two conditions up front, so a greyed-out EDIT
                  doesn't read as broken. */}
              <div className="functional-filter-edit-note">
                <FaInfoCircle size={11} className="text_primary mr-1" />
                <span>
                  You can only edit records you recorded yourself, and the
                  filters must narrow to a single assessment date.
                </span>
              </div>
            </div>

            {/* Batch selector — only shown when the filters resolve to TWO OR
                MORE current batches for the SAME assessment date. With a single
                batch we edit it directly, so no selection is needed; across
                different assessment dates the filter already tells them apart
                and Edit is disabled. The Edit button in the filter bar stays
                greyed out until the user checks one of these. */}
            {editableBatches.length > 1 && !batchesSpanMultipleDays && (
              <div className="mb-3 p-2 border rounded bg-light">
                <small className="font-weight-bold d-block mb-2">
                  Select a record to edit
                  {editableBatches[0]?.assessmentDay
                    ? ` (assessed ${moment(
                        editableBatches[0].assessmentDay,
                        "YYYY-MM-DD",
                      ).format("D MMM YYYY")}):`
                    : ":"}
                </small>
                <div className="d-flex flex-wrap">
                  {editableBatches.map((batch) => (
                    <label
                      key={batch.entryId}
                      className="d-flex align-items-center mr-4 mb-1"
                      style={{ cursor: "pointer" }}
                    >
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={selectedBatchEntryId === batch.entryId}
                        onChange={() =>
                          setSelectedBatchEntryId(
                            selectedBatchEntryId === batch.entryId
                              ? null
                              : batch.entryId,
                          )
                        }
                      />
                      <small>
                        Record created{" "}
                        {batch.createdAtIsInstant
                          ? `${formatInEt(
                              batch.createdAt,
                              "D MMM YYYY, h:mm A",
                            )} ET`
                          : moment(
                              batch.assessmentDay,
                              "YYYY-MM-DD",
                            ).format("D MMM YYYY")}
                        {batch.versionNumber > 1
                          ? ` (v${batch.versionNumber})`
                          : ""}
                      </small>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Edit reasons — read-only history of every amended version (V2+)
                for the selected lineage, newest first. Shown when a batch is
                selected, or when the filtered data resolves to a single
                lineage. */}
            {lineageReasons.length > 0 && (
              <div className="mb-3">
                {lineageReasons.map((v) => (
                  <div key={v.versionNumber}>
                    <small>
                      <span className="font-weight-bold">
                        V{v.versionNumber}:
                      </span>{" "}
                      {v.editReason}
                    </small>
                  </div>
                ))}
              </div>
            )}

            <div className="flex-grow-1 overflow-auto">
              {isLoading && (
                <SkeletonTheme baseColor="#e9ecef" highlightColor="#f8f9fa">
                  {[...Array(2)].map((_, index) => (
                    <div key={`skeleton-accordion-${index}`} className="mb-2">
                      {/* Accordion Header Skeleton */}
                      <div
                        className="accordion__primary accordion__button"
                        style={{
                          height: 45,
                          display: "flex",
                          alignItems: "center",
                          padding: "0 16px",
                          borderRadius: "4px",
                        }}
                      >
                        <small>
                          <Skeleton width={200} height={14} />
                        </small>
                      </div>

                      {/* Table Header Skeleton */}
                      <div className="d-flex justify-content-between text-center text-white head_light mx-0 h-27 small-text">
                        <div className="flex-grow-1" style={{ width: 170 }}>
                          <small className="d-flex justify-content-center align-items-center h-100">
                            Functional Issue
                          </small>
                        </div>
                        <div
                          className="border-left-1 d-flex align-items-center justify-content-center flex-grow-1"
                          style={{ width: 84 }}
                        >
                          <small className="d-flex justify-content-center align-items-center h-100">
                            Position
                          </small>
                        </div>
                        <div
                          className="border-left-1 d-flex align-items-center justify-content-center flex-grow-1"
                          style={{ width: 109 }}
                        >
                          <small className="d-flex justify-content-center align-items-center h-100">
                            Provider
                          </small>
                        </div>
                        <div
                          className="border-left-1 d-flex align-items-center justify-content-center flex-grow-1"
                          style={{ width: 119 }}
                        >
                          <small className="d-flex justify-content-center align-items-center h-100">
                            Assessment Date
                          </small>
                        </div>
                        <div
                          className="border-left-1 d-flex align-items-center justify-content-center flex-grow-1"
                          style={{ width: 17 }}
                        >
                          <small className="d-flex justify-content-center align-items-center h-100">
                            1
                          </small>
                        </div>
                        <div
                          className="border-left-1 d-flex align-items-center justify-content-center flex-grow-1"
                          style={{ width: 17 }}
                        >
                          <small className="d-flex justify-content-center align-items-center h-100">
                            2
                          </small>
                        </div>
                        <div
                          className="border-left-1 d-flex align-items-center justify-content-center flex-grow-1"
                          style={{ width: 17 }}
                        >
                          <small className="d-flex justify-content-center align-items-center h-100">
                            3
                          </small>
                        </div>
                        <div
                          className="border-left-1 d-flex align-items-center justify-content-center flex-grow-1"
                          style={{ width: 17 }}
                        >
                          <small className="d-flex justify-content-center align-items-center h-100">
                            4
                          </small>
                        </div>
                        <div
                          className="border-left-1 d-flex align-items-center justify-content-center flex-grow-1"
                          style={{ width: 16 }}
                        >
                          <small className="d-flex justify-content-center align-items-center h-100">
                            5
                          </small>
                        </div>
                        <div
                          className="border-left-1 d-flex align-items-center justify-content-center flex-grow-1"
                          style={{ width: 115 }}
                        >
                          <small className="d-flex justify-content-center align-items-center h-100">
                            Comments
                          </small>
                        </div>
                        <div
                          className="border-left-1 d-flex align-items-center justify-content-center"
                          style={{ width: 59, flexShrink: 0 }}
                        >
                          <small className="d-flex justify-content-center align-items-center h-100">
                            Median
                          </small>
                        </div>
                        <div
                          className="border-left-1 d-flex align-items-center justify-content-center"
                          style={{ width: 67, flexShrink: 0 }}
                        >
                          <small className="d-flex justify-content-center align-items-center h-100">
                            Team score
                          </small>
                        </div>
                      </div>

                      {/* Subheading Skeleton */}
                      <div className="d-flex justify-content-between bg_light my-1 h-35 mx-0">
                        <div
                          className="d-flex align-items-center"
                          style={{ flex: 1, paddingLeft: "12px" }}
                        >
                          <FaPlus size={11} />
                          &nbsp;
                          <small className="small-text">
                            <Skeleton width={150} height={12} />
                          </small>
                        </div>
                        <div className="d-flex align-items-center">
                          <div
                            className="d-flex justify-content-center align-items-center"
                            style={{ width: 59 }}
                          >
                            <Skeleton circle width={27} height={27} />
                          </div>
                          <div
                            className="d-flex justify-content-center align-items-center"
                            style={{ width: 67 }}
                          >
                            <Skeleton circle width={27} height={27} />
                          </div>
                        </div>
                      </div>

                      {/* Data Rows Skeleton */}
                      {[...Array(2)].map((_, rowIndex) => (
                        <div
                          key={`skeleton-row-${index}-${rowIndex}`}
                          className="d-flex justify-content-between mx-0 h-31 border-bottom-2"
                          style={{
                            backgroundColor:
                              rowIndex % 2 === 0 ? "#F1F1F1" : "#F8F8F8",
                          }}
                        >
                          <div
                            className="border-left-2 py-3 bg-white d-flex align-items-center flex-grow-1"
                            style={{ width: 170 }}
                          ></div>
                          <div
                            className="border-left-2 py-3 d-flex align-items-center justify-content-center flex-grow-1"
                            style={{ width: 84 }}
                          >
                            <Skeleton width={40} height={10} />
                          </div>
                          <div
                            className="border-left-2 py-3 d-flex align-items-center justify-content-center flex-grow-1"
                            style={{ width: 109 }}
                          >
                            <Skeleton width={80} height={10} />
                          </div>
                          <div
                            className="border-left-2 py-3 d-flex align-items-center justify-content-center flex-grow-1"
                            style={{ width: 119 }}
                          >
                            <Skeleton width={70} height={10} />
                          </div>
                          <div
                            className="border-left-2 d-flex align-items-center justify-content-center flex-grow-1"
                            style={{ width: 17 }}
                          >
                            <Skeleton circle width={12} height={12} />
                          </div>
                          <div
                            className="border-left-2 d-flex align-items-center justify-content-center flex-grow-1"
                            style={{ width: 17 }}
                          >
                            <Skeleton circle width={12} height={12} />
                          </div>
                          <div
                            className="border-left-2 d-flex align-items-center justify-content-center flex-grow-1"
                            style={{ width: 17 }}
                          >
                            <Skeleton circle width={12} height={12} />
                          </div>
                          <div
                            className="border-left-2 d-flex align-items-center justify-content-center flex-grow-1"
                            style={{ width: 17 }}
                          >
                            <Skeleton circle width={12} height={12} />
                          </div>
                          <div
                            className="border-left-2 d-flex align-items-center justify-content-center flex-grow-1"
                            style={{ width: 16 }}
                          >
                            <Skeleton circle width={12} height={12} />
                          </div>
                          <div
                            className="border-left-2 py-3 d-flex align-items-center justify-content-center flex-grow-1"
                            style={{ width: 115 }}
                          >
                            <Skeleton width={80} height={10} />
                          </div>
                          <div
                            className="border-left-2 py-3 d-flex align-items-center justify-content-center"
                            style={{ width: 59, flexShrink: 0 }}
                          >
                            <Skeleton width={20} height={10} />
                          </div>
                          <div
                            className="border-left-2 py-3 d-flex align-items-center justify-content-center"
                            style={{ width: 67, flexShrink: 0 }}
                          >
                            <Skeleton width={30} height={10} />
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </SkeletonTheme>
              )}
              {headings &&
                Array.isArray(headings) &&
                headings.map((heading, accUid) => (
                  <Accordion allowZeroExpanded preExpanded={[0]}>
                    <AccordionItem className="mb-2" uuid={accUid}>
                      <AccordionItemHeading className="accordion__primary">
                        <AccordionItemButton
                          style={{
                            height: 45,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <small>{heading.headingName}</small>
                        </AccordionItemButton>
                      </AccordionItemHeading>
                      <AccordionItemPanel>
                        <div className="d-flex justify-content-between text-center text-white head_light mx-0 h-27 small-text">
                          <div className="flex-grow-1" style={{ width: 170 }}>
                            <small className="d-flex justify-content-center align-items-center h-100 ">
                              Functional Issue
                            </small>
                          </div>
                          <div
                            className="border-left-1 d-flex align-items-center justify-content-center flex-grow-1"
                            style={{ width: 84 }}
                          >
                            <small className="d-flex justify-content-center align-items-center h-100">
                              Position
                            </small>
                          </div>
                          <div
                            className="border-left-1 d-flex align-items-center justify-content-center flex-grow-1"
                            style={{ width: 109 }}
                          >
                            <small className="d-flex justify-content-center align-items-center h-100">
                              Provider
                            </small>
                          </div>
                          <div
                            className="border-left-1 d-flex align-items-center justify-content-center flex-grow-1"
                            style={{ width: 119 }}
                          >
                            <small className="d-flex justify-content-center align-items-center h-100">
                              Assessment Date
                            </small>
                          </div>
                          {/* One column per value the heading's scale
                              defines — 1-5 for POMMe-Health, 0-4 for ICF,
                              1-10 for the ten-point scale. */}
                          {getScaleValues(heading.scoringScale).map((value) => (
                            <div
                              key={value}
                              className="border-left-1 d-flex align-items-center justify-content-center flex-grow-1"
                              style={{ width: 17 }}
                            >
                              <small className="d-flex justify-content-center align-items-center h-100">
                                {value}
                              </small>
                            </div>
                          ))}
                          <div
                            className=" border-left-1 d-flex align-items-center justify-content-center flex-grow-1"
                            style={{ width: 115 }}
                          >
                            <small className="d-flex justify-content-center align-items-center h-100">
                              Comments
                            </small>
                          </div>
                          <div
                            className=" border-left-1 d-flex align-items-center justify-content-center"
                            style={{ width: 59, flexShrink: 0 }}
                          >
                            <small className="d-flex justify-content-center align-items-center h-100">
                              Median
                            </small>
                          </div>
                          <div
                            className=" border-left-1 d-flex align-items-center justify-content-center"
                            style={{ width: 67, flexShrink: 0 }}
                          >
                            <small className="d-flex justify-content-center align-items-center h-100">
                              Team score
                            </small>
                          </div>
                        </div>
                        {heading.subheadings &&
                          Array.isArray(heading.subheadings) &&
                          heading.subheadings.map((subheading, key) => (
                            <Fragment key={key}>
                              <div className="d-flex bg_light my-1 h-35 mx-0">
                                <div
                                  className="d-flex align-items-center flex-grow-1"
                                  style={{
                                    minWidth: 170,
                                    paddingLeft: "12px",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  <a
                                    href="#"
                                    className="decoration-none"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      active.includes(subheading.subheadingName)
                                        ? removeActiveSubheading(
                                            active.indexOf(
                                              subheading.subheadingName,
                                            ),
                                          )
                                        : addActiveSubheading(
                                            subheading.subheadingName,
                                          );
                                    }}
                                  >
                                    {active.includes(
                                      subheading.subheadingName,
                                    ) ? (
                                      <FaMinus size={11} />
                                    ) : (
                                      <FaPlus size={11} />
                                    )}{" "}
                                    &nbsp;{" "}
                                    <small className="small-text">
                                      {subheading.subheadingName}
                                    </small>
                                  </a>
                                </div>
                                <div
                                  className="d-flex align-items-center justify-content-center flex-grow-1"
                                  style={{ width: 84 }}
                                ></div>
                                <div
                                  className="d-flex align-items-center justify-content-center flex-grow-1"
                                  style={{ width: 109 }}
                                ></div>
                                <div
                                  className="d-flex align-items-center justify-content-center flex-grow-1"
                                  style={{ width: 119 }}
                                ></div>
                                {getScaleValues(heading.scoringScale).map(
                                  (value) => (
                                    <div
                                      key={value}
                                      className="d-flex align-items-center justify-content-center flex-grow-1"
                                      style={{ width: 17 }}
                                    ></div>
                                  )
                                )}
                                <div
                                  className="d-flex align-items-center justify-content-center flex-grow-1"
                                  style={{ width: 115 }}
                                ></div>
                                <div
                                  className="d-flex align-items-center justify-content-center"
                                  style={{ width: 59, flexShrink: 0 }}
                                >
                                  <div
                                    className={` w-35 h-27  rounded-pill d-flex align-items-center justify-content-center ${
                                      getTeamSelectedScore(subheading.id)
                                        ? "bg_grey"
                                        : medianPill(
                                            subheading,
                                            heading.scoringScale,
                                          ).className
                                    }`}
                                    style={
                                      getTeamSelectedScore(subheading.id)
                                        ? undefined
                                        : medianPill(
                                            subheading,
                                            heading.scoringScale,
                                          ).style
                                    }
                                  >
                                    <span className="fs-14 font-weight-bold">
                                      {normalizeScore(
                                        Math.floor(subheading.median),
                                        heading.scoringScale,
                                      )}
                                    </span>
                                  </div>
                                </div>
                                <div
                                  className="d-flex align-items-center justify-content-center"
                                  style={{ width: 67, flexShrink: 0 }}
                                >
                                  <div
                                    className="dropdown"
                                    style={{ width: "100%" }}
                                  >
                                    {shouldShowDropdown(
                                      subheading,
                                      heading.scoringScale,
                                    ) ? (
                                      <>
                                        <button
                                          className={`btn btn-block border px-1 bg-white ${
                                            getTeamSelectedScore(
                                              subheading.id,
                                            ) && "bg-transparent border-0"
                                          }`}
                                          type="button"
                                          id={`dropdownMenuButton-${subheading.id}`}
                                          data-toggle="dropdown"
                                          aria-expanded="false"
                                          style={{ borderRadius: "4px" }}
                                        >
                                          <div className="d-flex align-items-center justify-content-between">
                                            {/* "N/A" is a choice like any
                                                score, so the pill replaces
                                                "Select" — but it stays blank,
                                                like an unscored row. */}
                                            {getTeamSelectedScore(
                                              subheading.id,
                                            ) ? (
                                              <div
                                                className={`rounded-pill d-flex align-items-center justify-content-center font-weight-bold ${
                                                  teamScorePillProps(
                                                    getTeamSelectedScore(
                                                      subheading.id,
                                                    ),
                                                    heading.scoringScale,
                                                  ).className
                                                }`}
                                                style={{
                                                  width: "28px",
                                                  height: "28px",
                                                  fontSize: "12px",
                                                  minWidth: "28px",
                                                  ...teamScorePillProps(
                                                    getTeamSelectedScore(
                                                      subheading.id,
                                                    ),
                                                    heading.scoringScale,
                                                  ).style,
                                                }}
                                              >
                                                {teamScorePillLabel(
                                                  getTeamSelectedScore(
                                                    subheading.id,
                                                  ),
                                                )}
                                              </div>
                                            ) : (
                                              <small className="f-11">
                                                Select
                                              </small>
                                            )}
                                            <BsChevronDown
                                              className="text-secondary"
                                              style={{ fontSize: "12px" }}
                                            />
                                          </div>
                                        </button>
                                        <div
                                          className="dropdown-menu small border-0 shadow"
                                          aria-labelledby={`dropdownMenuButton-${subheading.id}`}
                                        >
                                          <p className="pl-4 mb-0 pb-2">
                                            <small>
                                              Choose one score only:
                                            </small>
                                          </p>
                                          {getTeamSelectedScore(
                                            subheading.id,
                                          ) && (
                                            <div className="d-flex justify-content-end px-2 mb-2">
                                              <button
                                                type="button"
                                                className="functional-filter-reset"
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  clearTeamScore(subheading.id);
                                                  setHasTeamScoresBeenModified(
                                                    true,
                                                  );
                                                  sessionStorage.setItem(
                                                    "hasUnsavedTeamScores",
                                                    "true",
                                                  );
                                                }}
                                              >
                                                Clear
                                              </button>
                                            </div>
                                          )}
                                          {/* The team score is picked from
                                              the same scale the individual
                                              scores under this heading were
                                              recorded on. */}
                                          {getScore(heading.scoringScale)
                                            .sort((a, b) => {
                                              // Put N/A first, then sort numbers in descending order
                                              if (a.value === "N/A") return -1;
                                              if (b.value === "N/A") return 1;
                                              return b.value - a.value;
                                            })
                                            .map((score, key) => {
                                              const pill = teamScorePillProps(
                                                score.value,
                                                heading.scoringScale,
                                              );
                                              return (
                                                <a
                                                  className={`dropdown-item ml-4 border_primary mr-2 mb-1 rounded-5 h-35 ${
                                                    getTeamSelectedScore(
                                                      subheading.id,
                                                    ) === score.value
                                                      ? "bg-light"
                                                      : ""
                                                  }`}
                                                  key={key}
                                                  href="#"
                                                  onClick={(e) => {
                                                    e.preventDefault();
                                                    handleTeamScoreSelection(
                                                      subheading.id,
                                                      score.value,
                                                    );
                                                  }}
                                                >
                                                  <small
                                                    className={`w-27 h-27  rounded-circle d-flex align-items-center justify-content-center font-weight-bold ${pill.className}`}
                                                    style={pill.style}
                                                  >
                                                    {score.value}
                                                  </small>
                                                </a>
                                              );
                                            })}
                                        </div>
                                      </>
                                    ) : (
                                      <div className="d-flex align-items-center justify-content-center">
                                        <div
                                          className={`w-35 h-27 rounded-pill d-flex align-items-center justify-content-center font-weight-bold ${
                                            getScorePillProps(
                                              heading.scoringScale,
                                              getNumericScore(
                                                subheading.teamScore,
                                                heading.scoringScale,
                                              ),
                                              "bg_light",
                                            ).className
                                          }`}
                                          style={
                                            getScorePillProps(
                                              heading.scoringScale,
                                              getNumericScore(
                                                subheading.teamScore,
                                                heading.scoringScale,
                                              ),
                                              "bg_light",
                                            ).style
                                          }
                                        >
                                          <small className="text-dark font-weight-bold">
                                            {normalizeScore(
                                              subheading.teamScore,
                                              heading.scoringScale,
                                            )}
                                          </small>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                              {active.includes(subheading.subheadingName) &&
                                subheading.collaboratorScores
                                  .filter((score) =>
                                    hasScore(score, heading.scoringScale),
                                  )
                                  .map((score, key) => (
                                    <div
                                      key={key}
                                      className="d-flex justify-content-between mx-0 h-31 border-bottom-2"
                                      style={{
                                        backgroundColor:
                                          key % 2 === 0 ? "#F1F1F1" : "#F8F8F8",
                                        // Blur scores that are not the current
                                        // version of their lineage.
                                        ...(score.currentVersion === false
                                          ? {
                                              filter: "blur(0.6px)",
                                              opacity: 0.7,
                                              pointerEvents: "none",
                                            }
                                          : {}),
                                      }}
                                    >
                                      <div
                                        className="border-left-2 py-3 bg-white d-flex align-items-center justify-content-center flex-grow-1"
                                        style={{ width: 170 }}
                                      >
                                        {(score.versionNumber > 1 ||
                                          score.currentVersion === false) && (
                                          <span
                                            className="badge badge-secondary"
                                            style={{ fontSize: 9 }}
                                          >
                                            v{score.versionNumber}
                                          </span>
                                        )}
                                      </div>
                                      <div
                                        className="border-left-2 py-3 d-flex align-items-center fs-10 d-flex align-items-center justify-content-center flex-grow-1"
                                        style={{ width: 84 }}
                                      >
                                        {score.position.abbreviation}
                                      </div>
                                      <div
                                        className="border-left-2 py-3 fs-10 d-flex align-items-center justify-content-center flex-grow-1"
                                        style={{ width: 109 }}
                                      >
                                        <span>{`${score.lastname}, ${score.firstname}`}</span>
                                      </div>
                                      <div
                                        className="border-left-2 py-3 fs-10 d-flex align-items-center justify-content-center flex-grow-1"
                                        style={{ width: 119 }}
                                      >
                                        {formatInEt(
                                          score?.assessmentDate,
                                          "D MMM YYYY",
                                        )}
                                      </div>

                                      {/* Only records with a real score reach
                                          here, so the score always lands in one
                                          of the heading scale's columns. */}
                                      {getScaleValues(heading.scoringScale).map(
                                        (value) => {
                                          const pill = getScorePillProps(
                                            heading.scoringScale,
                                            value,
                                          );
                                          return (
                                            <div
                                              key={value}
                                              className="border-left-2 d-flex align-items-center justify-content-center flex-grow-1"
                                              style={{ width: 17 }}
                                            >
                                              {toScoreValue(
                                                score.score,
                                                heading.scoringScale,
                                              ) === value && (
                                                <div
                                                  className={`w-17 h-17 rounded-circle ${pill.className} d-flex align-items-center justify-content-center`}
                                                  style={pill.style}
                                                >
                                                  <span className="fs-10 font-weight-bold">
                                                    {value}
                                                  </span>
                                                </div>
                                              )}
                                            </div>
                                          );
                                        },
                                      )}

                                      <div
                                        className="border-left-2 pl-1 flex-grow-1 fs-10"
                                        style={{ width: 115 }}
                                      >
                                        {score.comment}
                                      </div>
                                      <div
                                        className="border-left-2 d-flex align-items-center justify-content-center fs-10"
                                        style={{ width: 59, flexShrink: 0 }}
                                      ></div>
                                      <div
                                        className="border-left-2 d-flex align-items-center justify-content-center fs-10"
                                        style={{ width: 67, flexShrink: 0 }}
                                      ></div>
                                    </div>
                                  ))}
                            </Fragment>
                          ))}
                      </AccordionItemPanel>
                    </AccordionItem>
                  </Accordion>
                ))}

              {/* Questions Accordion */}
              {questionsData?.length > 0 && (
                <Accordion allowZeroExpanded preExpanded={[0]}>
                  <AccordionItem className="mb-2" uuid="questions">
                    <AccordionItemHeading className="accordion__primary">
                      <AccordionItemButton
                        style={{
                          height: 45,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <small>Questions</small>
                      </AccordionItemButton>
                    </AccordionItemHeading>
                    <AccordionItemPanel>
                      <div className="d-flex justify-content-between text-center text-white head_light mx-0 h-27 small-text">
                        <div
                          className="flex-grow-1 bg-white"
                          style={{ width: 170 }}
                        >
                          <small className="d-flex justify-content-center align-items-center h-100">
                            {/* Empty space to match Functional Issue width */}
                          </small>
                        </div>
                        <div
                          className="border-left-1 d-flex align-items-center justify-content-center flex-grow-1"
                          style={{ width: 84 }}
                        >
                          <small className="d-flex justify-content-center align-items-center h-100">
                            Position
                          </small>
                        </div>
                        <div
                          className="border-left-1 d-flex align-items-center justify-content-center flex-grow-1"
                          style={{ width: 109 }}
                        >
                          <small className="d-flex justify-content-center align-items-center h-100">
                            Provider
                          </small>
                        </div>
                        <div
                          className="border-left-1 d-flex align-items-center justify-content-center flex-grow-1"
                          style={{ width: 119 }}
                        >
                          <small className="d-flex justify-content-center align-items-center h-100">
                            Assessment Date
                          </small>
                        </div>
                        <div
                          className="border-left-1 d-flex align-items-center justify-content-center flex-grow-1"
                          style={{ width: 570 }}
                        >
                          <small className="d-flex justify-content-center align-items-center h-100">
                            Comments
                          </small>
                        </div>
                      </div>
                      {questionsData &&
                        Array.isArray(questionsData) &&
                        questionsData.map((question, key) => (
                          <Fragment key={question.id || key}>
                            <div className="row mx-0 bg_light my-1 h-35">
                              <div className="col-md-12 d-flex align-items-center">
                                <a
                                  href="#"
                                  className="decoration-none"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    questionsActive.includes(question.id)
                                      ? removeActiveQuestion(
                                          questionsActive.indexOf(question.id),
                                        )
                                      : addActiveQuestion(question.id);
                                  }}
                                >
                                  {questionsActive.includes(question.id) ? (
                                    <FaMinus size={11} />
                                  ) : (
                                    <FaPlus size={11} />
                                  )}{" "}
                                  &nbsp;{" "}
                                  <small className="small-text">
                                    {question.questionText}
                                  </small>
                                </a>
                              </div>
                            </div>
                            {questionsActive.includes(question.id) &&
                              question.responses.map(
                                (response, responseKey) => (
                                  <div
                                    key={response.id || responseKey}
                                    className="d-flex justify-content-between mx-0 h-31 border-bottom-2"
                                    style={{
                                      backgroundColor:
                                        responseKey % 2 === 0
                                          ? "#F1F1F1"
                                          : "#F8F8F8",
                                    }}
                                  >
                                    <div
                                      className="border-left-2 py-3 bg-white d-flex align-items-center flex-grow-1"
                                      style={{ width: 170 }}
                                    >
                                      {/* Empty space to match Functional Issue width */}
                                    </div>
                                    <div
                                      className="border-left-2 py-3 d-flex align-items-center justify-content-center flex-grow-1"
                                      style={{ width: 84 }}
                                    >
                                      <span className="fs-10">
                                        {response.position?.abbreviation || "N/A"}
                                      </span>
                                    </div>
                                    <div
                                      className="border-left-2 py-3 fs-10 d-flex align-items-center justify-content-center flex-grow-1"
                                      style={{ width: 109 }}
                                    >
                                      <span>
                                        {response.lastname || response.firstname
                                          ? `${response.lastname}, ${response.firstname}`
                                          : "N/A"}
                                      </span>
                                    </div>
                                    <div
                                      className="border-left-2 py-3 fs-10 d-flex align-items-center justify-content-center flex-grow-1"
                                      style={{ width: 119 }}
                                    >
                                      {formatInEt(
                                        response.assessmentDate,
                                        "D MMM YYYY",
                                      )}
                                    </div>
                                    <div
                                      className="border-left-2 py-3 fs-10 d-flex align-items-center flex-grow-1"
                                      style={{ width: 570, padding: "8px" }}
                                    >
                                      <span
                                        style={{
                                          wordWrap: "break-word",
                                          lineHeight: "1.4",
                                        }}
                                      >
                                        {response.answer}
                                      </span>
                                    </div>
                                  </div>
                                ),
                              )}
                          </Fragment>
                        ))}
                    </AccordionItemPanel>
                  </AccordionItem>
                </Accordion>
              )}

              {headings?.length === 0 &&
                questionsData?.length === 0 &&
                !isLoading && (
                  <div className="d-flex justify-content-center align-items-center">
                    <p className="text_primary">No Data Found</p>
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
      <CustomFooter
        onDownload={hasExportableData() ? handleDownloadClick : null}
        onSign={handleSign}
        showSignButton={shouldShowSignButton()}
        signLabel={
          hasTeamScoresBeenModified ? "SAVE TEAM SCORES" : "GO TO CAREPLAN"
        }
      />

      {/* Hidden Print Component */}
      <div style={{ display: "none" }}>
        <div ref={printRef}>
          <div
            style={{
              padding: 20,
              fontFamily: "Arial, sans-serif",
              fontSize: 14,
              background: "#fff",
            }}
          >
            {/* Patient Data, Title, and Date in a Flex Row */}
            <div
              style={{
                display: "flex",
                gap: "200px",
                alignItems: "flex-start",
                marginBottom: "20px",
              }}
            >
              {/* Patient Data Section */}
              <div style={{ minWidth: 180 }}>
                <div
                  style={{ fontSize: 14, fontWeight: "bold", color: "#222" }}
                >
                  Patient
                </div>
                <div style={{ fontSize: 12, color: "#222", marginTop: 2 }}>
                  {
                    JSON.parse(localStorage.getItem("localPatient") || "{}")
                      ?.lastName
                  }
                  ,{" "}
                  {
                    JSON.parse(localStorage.getItem("localPatient") || "{}")
                      ?.firstName
                  }
                </div>
                <div style={{ fontSize: 12, color: "#222", marginTop: 2 }}>
                  {
                    JSON.parse(localStorage.getItem("localPatient") || "{}")
                      ?.age
                  }{" "}
                  {
                    JSON.parse(localStorage.getItem("localPatient") || "{}")
                      ?.gender
                  }
                </div>
                <div style={{ fontSize: 12, color: "#222", marginTop: 2 }}>
                  {JSON.parse(localStorage.getItem("localPatient") || "{}")?.dob
                    ? moment(
                        JSON.parse(localStorage.getItem("localPatient") || "{}")
                          ?.dob,
                      ).format("DD MMM YYYY")
                    : ""}
                </div>
                <div style={{ fontSize: 12, color: "#222", marginTop: 2 }}>
                  POMMe ID:{" "}
                  {
                    JSON.parse(localStorage.getItem("localPatient") || "{}")
                      ?.pommeId
                  }
                </div>
              </div>

              {/* Title/Header */}
              <div style={{ textAlign: "center" }}>
                <div
                  style={{ fontSize: 18, fontWeight: "bold", marginBottom: 5 }}
                >
                  Functional Summaries Report
                </div>

                {reportStartDate &&
                  reportEndDate &&
                  moment(reportStartDate).isValid() &&
                  moment(reportEndDate).isValid() && (
                    <p style={{ fontSize: 12, margin: 0 }}>
                      Time period:{" "}
                      {moment(reportStartDate).format("DD MMM YYYY")} to{" "}
                      {moment(reportEndDate).format("DD MMM YYYY")}
                    </p>
                  )}
              </div>
            </div>

            {/* Functional Summaries Data */}
            {headings &&
              Array.isArray(headings) &&
              headings.map((heading, headingIndex) => (
                <div key={headingIndex} style={{ marginBottom: 10 }}>
                  {/* Heading Bar */}
                  <div
                    style={{
                      background: "#5D8FA8",
                      color: "#fff",
                      padding: "8px 12px",
                      borderRadius: 4,
                      marginBottom: 2,
                      fontSize: 14,
                      fontWeight: "bold",
                    }}
                  >
                    {heading.headingName}
                  </div>

                  {heading.subheadings &&
                    Array.isArray(heading.subheadings) &&
                    heading.subheadings.map((subheading, subIndex) => (
                      <div key={subIndex} style={{ marginBottom: 15 }}>
                        {subheading.collaboratorScores?.length > 0 && (
                          <>
                            {/* Table */}
                            <table
                              style={{
                                width: "100%",
                                borderCollapse: "collapse",
                                marginBottom: 15,
                                fontSize: 10,
                              }}
                            >
                              <thead>
                                <tr
                                  style={{
                                    background: "#AFB1B8",
                                    color: "#fff",
                                  }}
                                >
                                  <th
                                    style={{
                                      padding: "8px",
                                      textAlign: "left",
                                      border: "1px solid #fff",
                                      fontSize: 9,
                                      fontWeight: "bold",
                                    }}
                                  >
                                    Functional Issue
                                  </th>
                                  <th
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      border: "1px solid #fff",
                                      fontSize: 9,
                                      fontWeight: "bold",
                                    }}
                                  >
                                    Position
                                  </th>
                                  <th
                                    style={{
                                      padding: "8px",
                                      textAlign: "left",
                                      border: "1px solid #fff",
                                      fontSize: 9,
                                      fontWeight: "bold",
                                    }}
                                  >
                                    Provider
                                  </th>
                                  <th
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      border: "1px solid #fff",
                                      fontSize: 9,
                                      fontWeight: "bold",
                                    }}
                                  >
                                    Assessment Date
                                  </th>
                                  <th
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      border: "1px solid #fff",
                                      fontSize: 9,
                                      fontWeight: "bold",
                                    }}
                                  >
                                    Score
                                  </th>
                                  <th
                                    style={{
                                      padding: "8px",
                                      textAlign: "left",
                                      border: "1px solid #fff",
                                      fontSize: 9,
                                      fontWeight: "bold",
                                    }}
                                  >
                                    Comments
                                  </th>
                                  <th
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      border: "1px solid #fff",
                                      fontSize: 9,
                                      fontWeight: "bold",
                                    }}
                                  >
                                    Median
                                  </th>
                                  <th
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      border: "1px solid #fff",
                                      fontSize: 9,
                                      fontWeight: "bold",
                                    }}
                                  >
                                    Team score
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {/* Title row for subheading */}
                                <tr
                                  style={{
                                    backgroundColor: "#EAF6F5", // Light gray for title row
                                    color: "#000",
                                    fontWeight: "bold",
                                  }}
                                >
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "left",
                                      border: "1px solid #fff",
                                    }}
                                  >
                                    {subheading.subheadingName}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      border: "1px solid #fff",
                                    }}
                                  >
                                    {/* Empty for title row */}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "left",
                                      border: "1px solid #fff",
                                    }}
                                  >
                                    {/* Empty for title row */}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      border: "1px solid #fff",
                                    }}
                                  >
                                    {/* Empty for title row */}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      border: "1px solid #fff",
                                    }}
                                  >
                                    {/* Empty for title row */}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "left",
                                      border: "1px solid #fff",
                                    }}
                                  >
                                    {/* Empty for title row */}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      border: "1px solid #fff",
                                    }}
                                  >
                                    {normalizeScore(
                                      Math.floor(subheading.median),
                                      heading.scoringScale,
                                    )}
                                  </td>
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      border: "1px solid #fff",
                                    }}
                                  >
                                    {normalizeScore(
                                      subheading.teamScore,
                                      heading.scoringScale,
                                    )}
                                  </td>
                                </tr>

                                {/* Provider entries - unanswered items are left
                                    out, N/A ones are listed with a blank
                                    score */}
                                {subheading.collaboratorScores
                                  .filter((score) =>
                                    hasScore(score, heading.scoringScale),
                                  )
                                  .map((score, scoreIndex) => (
                                    <tr
                                      key={scoreIndex}
                                      style={{
                                        backgroundColor: "#F5F5F5", // Light teal for provider rows
                                        color: "#000",
                                      }}
                                    >
                                      <td
                                        style={{
                                          padding: "8px 8px 8px 28px", // Indented
                                          textAlign: "left",
                                          border: "1px solid #fff",
                                          fontWeight: "500",
                                        }}
                                      ></td>
                                      <td
                                        style={{
                                          padding: "8px",
                                          textAlign: "center",
                                          border: "1px solid #fff",
                                        }}
                                      >
                                        {score.position?.abbreviation || "N/A"}
                                      </td>
                                      <td
                                        style={{
                                          padding: "8px",
                                          textAlign: "left",
                                          border: "1px solid #fff",
                                        }}
                                      >
                                        {`${score.lastname}, ${score.firstname}`}
                                      </td>
                                      <td
                                        style={{
                                          padding: "8px",
                                          textAlign: "center",
                                          border: "1px solid #fff",
                                        }}
                                      >
                                        {formatInEt(
                                          score.assessmentDate,
                                          "D MMM YYYY",
                                        )}
                                      </td>
                                      <td
                                        style={{
                                          padding: "8px",
                                          textAlign: "center",
                                          border: "1px solid #fff",
                                        }}
                                      >
                                        {contributorScore(
                                          score,
                                          heading.scoringScale,
                                        )}
                                      </td>
                                      <td
                                        style={{
                                          padding: "8px",
                                          textAlign: "left",
                                          border: "1px solid #fff",
                                        }}
                                      >
                                        {score.comment || "N/A"}
                                      </td>
                                      <td
                                        style={{
                                          padding: "8px",
                                          textAlign: "center",
                                          border: "1px solid #fff",
                                        }}
                                      >
                                        {/* Empty for provider rows */}
                                      </td>
                                      <td
                                        style={{
                                          padding: "8px",
                                          textAlign: "center",
                                          border: "1px solid #fff",
                                        }}
                                      >
                                        {/* Empty for provider rows */}
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </>
                        )}
                      </div>
                    ))}
                </div>
              ))}

            {/* Questions, once at the end: an answer belongs to the assessment
                rather than to any heading. */}
            {questionsData.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div
                  style={{
                    background: "#5D8FA8",
                    color: "#fff",
                    padding: "8px 12px",
                    borderRadius: 4,
                    marginBottom: 2,
                    fontSize: 14,
                    fontWeight: "bold",
                  }}
                >
                  Questions
                </div>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    marginBottom: 15,
                    fontSize: 10,
                  }}
                >
                  <thead>
                    <tr style={{ background: "#AFB1B8", color: "#fff" }}>
                      <th
                        style={{
                          padding: "8px",
                          textAlign: "left",
                          border: "1px solid #fff",
                          fontSize: 9,
                          fontWeight: "bold",
                        }}
                      >
                        Question
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          textAlign: "center",
                          border: "1px solid #fff",
                          fontSize: 9,
                          fontWeight: "bold",
                        }}
                      >
                        Position
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          textAlign: "left",
                          border: "1px solid #fff",
                          fontSize: 9,
                          fontWeight: "bold",
                        }}
                      >
                        Provider
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          textAlign: "center",
                          border: "1px solid #fff",
                          fontSize: 9,
                          fontWeight: "bold",
                        }}
                      >
                        Assessment Date
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          textAlign: "left",
                          border: "1px solid #fff",
                          fontSize: 9,
                          fontWeight: "bold",
                        }}
                      >
                        Answer
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {questionsData.map((question) => (
                      <Fragment key={question.id}>
                        <tr style={{ backgroundColor: "#EAF6F5" }}>
                          <td
                            style={{
                              padding: "8px",
                              textAlign: "left",
                              border: "1px solid #fff",
                              fontWeight: "bold",
                            }}
                            colSpan={5}
                          >
                            {question.questionText}
                          </td>
                        </tr>
                        {question.responses.map((response) => (
                          <tr
                            key={response.id}
                            style={{ backgroundColor: "#F5F5F5" }}
                          >
                            <td
                              style={{
                                padding: "8px 8px 8px 28px",
                                border: "1px solid #fff",
                              }}
                            ></td>
                            <td
                              style={{
                                padding: "8px",
                                textAlign: "center",
                                border: "1px solid #fff",
                              }}
                            >
                              {response.position?.abbreviation || "N/A"}
                            </td>
                            <td
                              style={{
                                padding: "8px",
                                textAlign: "left",
                                border: "1px solid #fff",
                              }}
                            >
                              {response.lastname || response.firstname
                                ? `${response.lastname}, ${response.firstname}`
                                : "N/A"}
                            </td>
                            <td
                              style={{
                                padding: "8px",
                                textAlign: "center",
                                border: "1px solid #fff",
                              }}
                            >
                              {formatInEt(
                                response.assessmentDate,
                                "D MMM YYYY",
                              )}
                            </td>
                            <td
                              style={{
                                padding: "8px",
                                textAlign: "left",
                                border: "1px solid #fff",
                              }}
                            >
                              {response.answer}
                            </td>
                          </tr>
                        ))}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {headings?.length === 0 && questionsData.length === 0 && (
              <div style={{ textAlign: "center", padding: 40 }}>
                <p style={{ color: "#666", fontStyle: "italic" }}>
                  No Data Found
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal Implementatio*/}
      <div
        className="modal fade"
        id="filterModal"
        tabindex="-1"
        aria-labelledby="exampleModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog">
          <div className="modal-content pt-3">
            <div className="modal-header border-0">
              <div className="flex-grow-1 text-center">
                <h5 className="text_primary" style={{ fontWeight: 600 }}>
                  Search Filter
                </h5>
              </div>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <span
                  className="text_primary"
                  style={{ fontSize: 40, fontWeight: "600" }}
                  aria-hidden="true"
                >
                  &times;
                </span>
              </button>
            </div>
            <div className="modal-body mb-3 p-0">
              <div className="d-flex justify-content-between align-items-center mb-4 px-3">
                <button
                  className="btn bg_dark darkHover"
                  onClick={handleTodayFilter}
                >
                  <small>Today</small>
                </button>
                <button
                  className="btn bg_dark darkHover"
                  onClick={handleLast7DaysFilter}
                >
                  <small>Last 7 days</small>
                </button>
                <button
                  className="btn bg_dark darkHover"
                  onClick={handleLast30DaysFilter}
                >
                  <small>Last 30 days</small>
                </button>
              </div>
              <small className="text-muted ml-3">Custom Date</small>
              <hr className="my-2 mx-3" />
              <div className="row mb-3 mx-1">
                <div className="col-md-6">
                  <small className="text-muted">Start Date</small>
                  <PickDate
                    date={startDate}
                    setDate={handleStartDateChange}
                    required={false}
                  />
                </div>
                <div className="col-md-6">
                  <small className="text-muted">End Date</small>
                  <PickDate
                    date={endDate}
                    setDate={handleEndDateChange}
                    required={false}
                  />
                </div>
              </div>

              <Accordion allowZeroExpanded allowMultipleExpanded>
                <div className="chevron" />
                <AccordionItem uuid="functional-issues-main">
                  <div className="d-flex justify-content-between align-items-center pr-3">
                    <AccordionItemHeading className="accordion_transparent">
                      <AccordionItemButton>
                        <small className="text-muted">Functional Issues</small>
                      </AccordionItemButton>
                    </AccordionItemHeading>
                    <Switch
                      checked={checked.attr === "function" && checked.check}
                      onChange={handleChecked}
                      onColor="#C7E6F5"
                      onHandleColor="#50A193"
                      handleDiameter={25}
                      uncheckedIcon={false}
                      checkedIcon={false}
                      boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                      // activeBoxShadow="0px 0px 1px 5px rgba(0, 0, 0, 0.2)"
                      height={16}
                      width={48}
                      className="react-switch"
                      id="function"
                    />
                  </div>
                  <AccordionItemPanel className="bg_lighter_primary m-0 px-4">
                    <Accordion allowZeroExpanded allowMultipleExpanded>
                      {checklistLoading && (
                        <div className="ml-4 py-2">
                          <small className="text-muted">
                            Loading functional issues...
                          </small>
                        </div>
                      )}
                      {functionalIssues &&
                        functionalIssues?.data[0]?.headings?.map(
                          (heading, headingIndex) => (
                            <AccordionItem
                              key={heading.id}
                              uuid={`functional-heading-${heading.id}`}
                            >
                              <AccordionItemHeading className="accordion_transparent">
                                <AccordionItemButton>
                                  <small className="text-muted">
                                    {heading.name}
                                  </small>
                                </AccordionItemButton>
                              </AccordionItemHeading>
                              <AccordionItemPanel className="ml-3">
                                <small className="text-muted f-13">
                                  Choose as many as applicable:
                                </small>
                                <div className="mt-2">
                                  {heading.subheadings.map((item, i) => (
                                    <div
                                      className="dropdown-item noFilterHover form-group form-check ml-4 wa mb-2"
                                      key={i}
                                    >
                                      <input
                                        type="checkbox"
                                        className="form-check-input mt-2"
                                        id={`functionalIssueCheck${item.id}`}
                                        checked={selectedFunctionalIssues.includes(
                                          item.id,
                                        )}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setSelectedFunctionalIssues([
                                              ...selectedFunctionalIssues,
                                              item.id,
                                            ]);
                                          } else {
                                            setSelectedFunctionalIssues(
                                              selectedFunctionalIssues.filter(
                                                (id) => id !== item.id,
                                              ),
                                            );
                                          }
                                        }}
                                        disabled={!checked.check}
                                      />
                                      <label
                                        className="form-check-label f-13 ml-2"
                                        htmlFor={`functionalIssueCheck${item.id}`}
                                      >
                                        <small>{item.name}</small>
                                      </label>
                                    </div>
                                  ))}
                                </div>
                              </AccordionItemPanel>
                            </AccordionItem>
                          ),
                        )}
                      {!checklistLoading &&
                        (!functionalIssues ||
                          !functionalIssues?.data[0]?.headings ||
                          functionalIssues?.data[0]?.headings?.length ===
                            0) && (
                          <div className="ml-4 py-2">
                            <small className="text-muted">
                              No functional issues available
                            </small>
                          </div>
                        )}
                      {/* <AccordionItem>
                        <AccordionItemHeading className="accordion_transparent">
                          <AccordionItemButton>
                            <small className="text-muted">Heading</small>
                          </AccordionItemButton>
                        </AccordionItemHeading>
                        <AccordionItemPanel className="ml-3">
                          <small className="text-muted f-13">
                            Choose as many as applicable:
                          </small>
                          <div className="mt-2">
                            {[1, 2, 3, 4].map((item, i) => (
                              <div
                                className="dropdown-item noFilterHover form-group form-check ml-4 wa mb-2"
                                key={i}
                              >
                                <input
                                  type="checkbox"
                                  className="form-check-input mt-2"
                                  id="exampleCheck1"
                                />
                                <label
                                  className="form-check-label f-13"
                                  for="exampleCheck1"
                                >
                                  <small>Subheading</small>
                                </label>
                              </div>
                            ))}
                          </div>
                        </AccordionItemPanel>
                      </AccordionItem>
                      <AccordionItem>
                        <AccordionItemHeading className="accordion_transparent">
                          <AccordionItemButton>
                            <small className="text-muted">Heading</small>
                          </AccordionItemButton>
                        </AccordionItemHeading>
                        <AccordionItemPanel className="ml-3">
                          <small className="text-muted f-13">
                            Choose as many as applicable:
                          </small>
                          <div className="mt-2">
                            {[1, 2, 3, 4].map((item, i) => (
                              <div
                                className="dropdown-item noFilterHover form-group form-check ml-4 wa mb-2"
                                key={i}
                              >
                                <input
                                  type="checkbox"
                                  className="form-check-input mt-2"
                                  id="exampleCheck1"
                                />
                                <label
                                  className="form-check-label f-13"
                                  for="exampleCheck1"
                                >
                                  <small>Subheading</small>
                                </label>
                              </div>
                            ))}
                          </div>
                        </AccordionItemPanel>
                      </AccordionItem> */}
                    </Accordion>
                  </AccordionItemPanel>
                </AccordionItem>
                <AccordionItem uuid="scores-filter">
                  <div className="d-flex justify-content-between align-items-center pr-3">
                    <AccordionItemHeading className="accordion_transparent">
                      <AccordionItemButton>
                        <small className="text-muted">Scores</small>
                      </AccordionItemButton>
                    </AccordionItemHeading>
                    <Switch
                      checked={
                        checkedScores.attr === "score" && checkedScores.check
                      }
                      onChange={handleChecked}
                      onColor="#C7E6F5"
                      onHandleColor="#50A193"
                      handleDiameter={25}
                      uncheckedIcon={false}
                      checkedIcon={false}
                      boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                      // activeBoxShadow="0px 0px 1px 5px rgba(0, 0, 0, 0.2)"
                      height={16}
                      width={48}
                      className="react-switch"
                      id="score"
                    />
                  </div>
                  <AccordionItemPanel className="bg_lighter_primary m-0 py-2 px-4">
                    <small className="text-muted f-13">Choose one score:</small>
                    <div className="d-flex align-items-center mt-2">
                      {scoreOptions.map(({ value: item }, i) => (
                        <div
                          className="dropdown-item noFilterHover form-group form-check ml-4 wa mb-1"
                          key={i}
                        >
                          <input
                            type="radio"
                            className="form-check-input mt-2"
                            id={`scoreRadio${item}`}
                            name="scoreRadio"
                            checked={selectedScore === item}
                            onChange={() => handleScoreSelection(item)}
                            disabled={!checkedScores.check}
                          />
                          <label
                            className="form-check-label f-13 ml-2"
                            htmlFor={`scoreRadio${item}`}
                          >
                            <small>
                              {i === 0 ? (
                                String(item)
                              ) : (
                                <>
                                  <span
                                    style={{
                                      fontSize: "16px",
                                    }}
                                  >
                                    ≤
                                  </span>{" "}
                                  {item}
                                </>
                              )}
                            </small>
                          </label>
                        </div>
                      ))}
                    </div>
                  </AccordionItemPanel>
                </AccordionItem>
                {/* <AccordionItem>
                  <div className="d-flex justify-content-between align-items-center pr-3">
                    <AccordionItemHeading className="accordion_transparent">
                      <AccordionItemButton>
                        <small className="text-muted">Role</small>
                      </AccordionItemButton>
                    </AccordionItemHeading>
                    <Switch
                      checked={checked.attr == "role" && checked.check}
                      onChange={handleChecked}
                      onColor="#C7E6F5"
                      onHandleColor="#50A193"
                      handleDiameter={25}
                      uncheckedIcon={false}
                      checkedIcon={false}
                      boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                      // activeBoxShadow="0px 0px 1px 5px rgba(0, 0, 0, 0.2)"
                      height={16}
                      width={48}
                      className="react-switch"
                      id="role"
                    />
                  </div>
                  <AccordionItemPanel className="bg_lighter_primary m-0 py-2 px-4">
                    <small className="text-muted f-13">
                      Choose as many as applicable:
                    </small>
                    <div className="mt-2">
                      {["HCP", "Family", "Patient"].map((item, i) => (
                        <div
                          className="dropdown-item noFilterHover form-group form-check ml-4 wa mb-1"
                          key={i}
                        >
                          <input
                            type="checkbox"
                            className="form-check-input mt-2"
                            id="exampleCheck1"
                          />
                          <label
                            className="form-check-label f-13"
                            for="exampleCheck1"
                          >
                            <small>{item}</small>
                          </label>
                        </div>
                      ))}
                    </div>
                  </AccordionItemPanel>
                </AccordionItem>
                <AccordionItem>
                  <div className="d-flex justify-content-between align-items-center pr-3">
                    <AccordionItemHeading className="accordion_transparent">
                      <AccordionItemButton>
                        <small className="text-muted">Position</small>
                      </AccordionItemButton>
                    </AccordionItemHeading>
                    <Switch
                      checked={checked.attr == "position" && checked.check}
                      onChange={handleChecked}
                      onColor="#C7E6F5"
                      onHandleColor="#50A193"
                      handleDiameter={25}
                      uncheckedIcon={false}
                      checkedIcon={false}
                      boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                      // activeBoxShadow="0px 0px 1px 5px rgba(0, 0, 0, 0.2)"
                      height={16}
                      width={48}
                      className="react-switch"
                      id="position"
                    />
                  </div>
                  <AccordionItemPanel className="bg_lighter_primary m-0 py-2 px-4">
                    <small className="text-muted f-13">
                      Choose as many as applicable:
                    </small>
                    <div className="mt-2">
                      {["Position name", "Position name", "Position name"].map(
                        (item, i) => (
                          <div
                            className="dropdown-item noFilterHover form-group form-check ml-4 wa mb-1"
                            key={i}
                          >
                            <input
                              type="checkbox"
                              className="form-check-input mt-2"
                              id="exampleCheck1"
                            />
                            <label
                              className="form-check-label f-13"
                              for="exampleCheck1"
                            >
                              <small>{item}</small>
                            </label>
                          </div>
                        )
                      )}
                    </div>
                  </AccordionItemPanel>
                </AccordionItem> */}
                <AccordionItem uuid="names-filter">
                  <div className="d-flex justify-content-between align-items-center pr-3">
                    <AccordionItemHeading className="accordion_transparent">
                      <AccordionItemButton>
                        <small className="text-muted">
                          Names (in selected role)
                        </small>
                      </AccordionItemButton>
                    </AccordionItemHeading>
                    <Switch
                      checked={
                        checkedNames.attr === "names" && checkedNames.check
                      }
                      onChange={handleChecked}
                      onColor="#C7E6F5"
                      onHandleColor="#50A193"
                      handleDiameter={25}
                      uncheckedIcon={false}
                      checkedIcon={false}
                      boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                      // activeBoxShadow="0px 0px 1px 5px rgba(0, 0, 0, 0.2)"
                      height={16}
                      width={48}
                      className="react-switch"
                      id="names"
                    />
                  </div>
                  <AccordionItemPanel className="bg_lighter_primary m-0 py-2 px-4">
                    <small className="text-muted f-13">
                      Choose as many as applicable:
                    </small>
                    <div className="mt-2">
                      {availableProviders.map((provider, i) => (
                        <div
                          className="dropdown-item noFilterHover form-group form-check ml-4 wa mb-1"
                          key={i}
                        >
                          <input
                            type="checkbox"
                            className="form-check-input mt-2"
                            id={`providerCheck${provider.id}`}
                            checked={selectedProviders.includes(provider.id)}
                            onChange={(e) =>
                              handleProviderSelection(
                                provider.id,
                                e.target.checked,
                              )
                            }
                            disabled={!checkedNames.check}
                          />
                          <label
                            className="form-check-label f-13 ml-2"
                            htmlFor={`providerCheck${provider.id}`}
                          >
                            <small>{provider.fullName}</small>
                          </label>
                        </div>
                      ))}
                      {availableProviders?.length === 0 && (
                        <div className="ml-4">
                          <small className="text-muted">
                            No providers found in current data
                          </small>
                        </div>
                      )}
                    </div>
                  </AccordionItemPanel>
                </AccordionItem>
              </Accordion>
            </div>
            <div className="modal-footer d-flex justify-content-between align-items-center border-0">
              <button
                type="button"
                className="btn btn_outline_danger px-5"
                onClick={handleClearFilters}
                data-dismiss="modal"
              >
                CLEAR
              </button>
              <button
                type="button"
                className="btn btn-outline-primary px-5"
                onClick={handleSearch}
              >
                SEARCH
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Download Template Chooser Modal */}
      <div
        className={`modal fade ${showDownloadModal ? "show d-block" : ""}`}
        tabIndex="-1"
        style={{
          backgroundColor: showDownloadModal
            ? "rgba(0,0,0,0.5)"
            : "transparent",
        }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header border-0 d-flex justify-content-center">
              <div className="text-center">
                <h5 className="modal-title font-weight-bold">Download PDF</h5>
              </div>
            </div>
            <div className="modal-body text-center px-4">
              <b className="text_default fs-13">
                Choose which template to download:
              </b>
              <p className="text_default fs-13 mt-3">
                <b>Full template</b> — All headings, subheadings and every
                individual score.
              </p>
              <p className="text_default fs-13">
                <b>Summary template</b> — All headings and subheadings with
                their median and team scores only.
              </p>
            </div>
            <div className="modal-footer border-0 d-flex justify-content-between px-4 pb-4">
              <button
                type="button"
                className="btn btn_outline_danger px-4"
                onClick={() => setShowDownloadModal(false)}
              >
                <small>CANCEL</small>
              </button>
              <div className="d-flex">
                <button
                  type="button"
                  className="btn btn-outline-primary px-3 mr-2"
                  onClick={() => handleExportPDF("summary")}
                >
                  <small>SUMMARY TEMPLATE</small>
                </button>
                <button
                  type="button"
                  className="btn btn-primary px-3"
                  onClick={() => handleExportPDF("full")}
                >
                  <small>FULL TEMPLATE</small>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Use-medians-as-team-scores confirmation, on the way to the care plan */}
      <div
        className={`modal fade ${showUseMediansModal ? "show d-block" : ""}`}
        tabIndex="-1"
        style={{
          backgroundColor: showUseMediansModal
            ? "rgba(0,0,0,0.5)"
            : "transparent",
        }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header border-0 d-flex justify-content-center">
              <div className="text-center">
                <FaExclamationTriangle
                  size={40}
                  className="text-warning mb-2"
                />
                <h5 className="modal-title text_primary font-weight-bold">
                  No Team Scores Assigned
                </h5>
              </div>
            </div>
            <div className="modal-body text-center px-4">
              <p className="text_default fs-13">
                Some functional issues have no team score for{" "}
                {reportStartDate && moment(reportStartDate).isValid()
                  ? moment(reportStartDate).format("D MMM YYYY")
                  : "this period"}{" "}
                –{" "}
                {reportEndDate && moment(reportEndDate).isValid()
                  ? moment(reportEndDate).format("D MMM YYYY")
                  : ""}
                . Do you want to use the medians across all providers as the
                team scores for the Collaborative Care Plan?
              </p>
              <p className="text_default fs-13">
                <b>Note:</b> The medians fill the current score on every
                functional issue that has one for this period. Choosing NO opens
                the care plan without them.
              </p>
            </div>
            <div className="modal-footer border-0 d-flex justify-content-between px-4 pb-4">
              <button
                type="button"
                className="btn btn_outline_danger px-4"
                onClick={goToCarePlanWithoutTeamScores}
              >
                <small>NO, CONTINUE</small>
              </button>
              <button
                type="button"
                className="btn btn-outline-primary px-3"
                onClick={goToCarePlanWithMedians}
              >
                <small>YES, USE MEDIANS</small>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Sign Confirmation Modal */}
      <div
        className={`modal fade ${showSignModal ? "show d-block" : ""}`}
        tabIndex="-1"
        style={{
          backgroundColor: showSignModal ? "rgba(0,0,0,0.5)" : "transparent",
        }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header border-0 d-flex justify-content-center">
              <div className="text-center">
                <h5 className="modal-title font-weight-bold">
                  SAVING Team Scores:
                </h5>
              </div>
            </div>
            <div className="modal-body text-center px-4">
              <p className="text_default fs-13">
                Team Scores will be used for the Case Conference to develop the
                Collaborative Care Plan.
              </p>
              <p className="text_default mt-4 fs-13">
                Once signed, they will be attached to the Collaborative Care
                Plan.
              </p>
            </div>
            <div className="modal-footer border-0 d-flex justify-content-between px-4 pb-4">
              <button
                type="button"
                className="btn btn_outline_danger px-4"
                onClick={handleCancelSign}
              >
                <small>GO BACK</small>
              </button>
              <button
                type="button"
                className="btn btn-outline-primary px-3"
                onClick={handleConfirmSign}
                disabled={isSubmittingScores}
              >
                <small>
                  {isSubmittingScores
                    ? "SIGNING..."
                    : "SIGN & Go To CREATE Care Plan"}
                </small>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Confirmation Modal */}
      <div
        className={`modal fade ${showNavigationModal ? "show d-block" : ""}`}
        tabIndex="-1"
        style={{
          backgroundColor: showNavigationModal
            ? "rgba(0,0,0,0.5)"
            : "transparent",
        }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header border-0 d-flex justify-content-center">
              <div className="text-center">
                <FaExclamationTriangle
                  size={40}
                  className="text-warning mb-2"
                />
                <h5 className="modal-title text_primary font-weight-bold">
                  Warning
                </h5>
              </div>
            </div>
            <div className="modal-body text-center px-4">
              <p className="text_default fs-13">
                Are you sure you want to leave this screen?
              </p>
              <p className="text_default fs-13">
                <b>Note:</b> All unsaved team scores will be lost
              </p>
            </div>
            <div className="modal-footer border-0 d-flex justify-content-between px-4 pb-4">
              <button
                type="button"
                className="btn btn_outline_danger px-4"
                onClick={handleCancelNavigation}
              >
                <small>GO BACK</small>
              </button>
              <button
                type="button"
                className="btn btn-outline-primary px-4"
                onClick={handleConfirmNavigation}
              >
                <small>YES</small>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FunctionalSummaries;
